/**
 * name: @eyejack/ejx
 * version: v0.1.2
 * date: 2024-01-12 20:41:38
 * description: EyaJack X - new Canvas for XR Experiences
 * author: EyeJack X
 * homepage: https://eyejack.xyz
 */
import { Vector3, Frustum, Line3, Triangle, Ray, Box2, Vector2, BufferGeometry, Float32BufferAttribute, Raycaster, MathUtils, Mesh, BackSide, FrontSide, Color, RawShaderMaterial, Vector4, DoubleSide, sRGBEncoding, RepeatWrapping, NearestFilter, LinearEncoding, TextureLoader, BoxGeometry, MeshBasicMaterial, Object3D, PlaneGeometry, OrthographicCamera, ShaderMaterial, UniformsUtils, WebGLRenderTarget, Clock, AdditiveBlending, ColorManagement, WebGLRenderer, Plane, NoToneMapping, PerspectiveCamera, Scene, ClampToEdgeWrapping, LinearFilter, RGBAFormat, UnsignedByteType, Texture, LoadingManager, Matrix3, Matrix4, Box3, Euler, Quaternion, Group } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

/* eslint-disable max-len */

const EJUtils = Object.create(null);

EJUtils.clamp = (x, a, b) => Math.max(Math.min(x, Math.max(a, b)), Math.min(a, b));
EJUtils.lerp = (a, b, t) => (b - a) * t + a;
EJUtils.unlerp = (a, b, t) => (t - a) / (b - a);
EJUtils.map = (a1, b1, a2, b2, t) => EJUtils.clamp(EJUtils.lerp(a2, b2, EJUtils.unlerp(a1, b1, t)), a2, b2);

EJUtils.disposeMaterial = material => {
  // in case of map, bumpMap, normalMap, envMap ...
  Object.keys(material).forEach(prop => {
    if (!material[prop]) {
      return;
    }
    if (material[prop] !== null && typeof material[prop].dispose === 'function') {
      material[prop].dispose();
    }
  });
  material.dispose();
};

EJUtils.disposeRecursive = obj => {
  while (obj.children.length > 0) {
    disposeRecursive(obj.children[0]);
    obj.remove(obj.children[0]);
  }
  if (obj.geometry) obj.geometry.dispose();

  if (obj.material) {
    if (Array.isArray(obj.material)) {
      obj.material.forEach(EJUtils.disposeMaterial);
    } else {
      EJUtils.disposeMaterial(obj.material);
    }
  }
};

EJUtils.clamp;
EJUtils.lerp;
EJUtils.unlerp;
EJUtils.map;
const disposeRecursive = EJUtils.disposeRecursive;

/* eslint-disable max-len */

const EJCube = Object.create(null);

// ---------------------------------------------------------------- Cube Geometry
EJCube.getFaceNormals = () => {
  const faceNormals = [];
  faceNormals.push(new Vector3(0, 0, 1)); // front
  faceNormals.push(new Vector3(1, 0, 0)); // right
  faceNormals.push(new Vector3(0, 0, -1)); // back
  faceNormals.push(new Vector3(-1, 0, 0)); // left
  faceNormals.push(new Vector3(0, 1, 0)); // top
  faceNormals.push(new Vector3(0, -1, 0)); // bottom
  return faceNormals;
};
const getFaceNormals = EJCube.getFaceNormals;

EJCube.getFaceCentres = () => {
  const faceCentres = [];
  faceCentres.push(new Vector3(0, 0, 0.5)); // front
  faceCentres.push(new Vector3(0.5, 0, 0)); // right
  faceCentres.push(new Vector3(0, 0, -0.5)); // back
  faceCentres.push(new Vector3(-0.5, 0, 0)); // left
  faceCentres.push(new Vector3(0, 0.5, 0)); // top
  faceCentres.push(new Vector3(0, -0.5, 0)); // bottom
  return faceCentres;
};
const getFaceCentres = EJCube.getFaceCentres;

EJCube.getFaceColorMasks = () => {
  const faceColorMasks = [];
  faceColorMasks.push(new Vector3(1, 0, 0)); // front
  faceColorMasks.push(new Vector3(0, 1, 0)); // right
  faceColorMasks.push(new Vector3(0, 0, 1)); // back
  faceColorMasks.push(new Vector3(1, 0, 1)); // left
  faceColorMasks.push(new Vector3(1, 1, 0)); // top
  faceColorMasks.push(new Vector3(0, 1, 1)); // bottom
  return faceColorMasks;
};
EJCube.getFaceColorMasks;

EJCube.getFaceDescriptors = () => {
  const faceDescriptors = [];
  faceDescriptors.push('front');
  faceDescriptors.push('right');
  faceDescriptors.push('back');
  faceDescriptors.push('left');
  faceDescriptors.push('top');
  faceDescriptors.push('bottom');
  return faceDescriptors;
};
EJCube.getFaceDescriptors;

EJCube.getFacePoints = ( faceIndex, matrixWorld) => {
  const faceCentres = getFaceCentres();
  /*
     (-0.5, 0.5)  0----1  (0.5, 0.5)
                  |    |
                  |    |
    (-0.5, -0.5)  3----2  (0.5, -0.5)
  */
  const points = [
    new Vector3(-0.5, 0.5, 0.0),
    new Vector3(0.5, 0.5, 0.0),
    new Vector3(0.5, -0.5, 0.0),
    new Vector3(-0.5, -0.5, 0.0),
  ];
  points.forEach(point => {
    if (faceIndex === 0) ; else if (faceIndex === 1) {
      point.applyAxisAngle( new Vector3(0, 1, 0), Math.PI * 0.5 );
    } else if (faceIndex === 2) {
      point.applyAxisAngle( new Vector3(0, 1, 0), Math.PI );
    } else if (faceIndex === 3) {
      point.applyAxisAngle( new Vector3(0, 1, 0), -Math.PI * 0.5 );
    } else if (faceIndex === 4) {
      point.applyAxisAngle( new Vector3(1, 0, 0), -Math.PI * 0.5 );
    } else if (faceIndex === 5) {
      point.applyAxisAngle( new Vector3(1, 0, 0), Math.PI * 0.5 );
    }
    point.add( faceCentres[faceIndex] );
    point.applyMatrix4( matrixWorld );
  });
  return points;
};
const getFacePoints = EJCube.getFacePoints;

EJCube.getFaceOnScreen = ( faceIndex, matrixWorld, camera ) => {
  const frustum = new Frustum();
  frustum.setFromProjectionMatrix(camera.projectionMatrix);
  frustum.planes[5].constant = 0; // reduce near clipping plane to zero.

  const planesContainPoint = (planes, point) => {
    for ( let i = 0; i < planes.length; i ++ ) {
      if ( planes[i].distanceToPoint( point ) < 0 ) {
        return false;
      }
    }
    return true;
  };

  const points = getFacePoints( faceIndex, matrixWorld );
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    point.applyMatrix4( camera.matrixWorldInverse );
    const insideFrustum = planesContainPoint(frustum.planes, point);
    if ( insideFrustum ) {
      return true; // at least one point found in frustum. means face is visible.
    }
  }

  // go through each frustum plane and check if all points are outside of the plane.
  // if all points are outside of the plane, its safe to assume that they are all offscreen.
  for ( let i = 0; i < frustum.planes.length; i ++ ) {
    const plane = frustum.planes[i];
    let pointsOutsidePlane = true;
    for ( let j = 0; j < points.length; j++ ) {
      const point = points[j];
      const pointOutsidePlane = ( plane.distanceToPoint( point ) < 0 );
      pointsOutsidePlane = pointsOutsidePlane && pointOutsidePlane;
    }
    if ( pointsOutsidePlane ) {
      return false;
    }
  }

  // check for triangle intersects with the frustum.
  // this happens when points are outside of the frustum but the edges of the triangles are intersecting the frustum,
  // which means that the triangles are visible.
  /*
     (-0.5, 0.5)  0---1  (0.5, 0.5)
                  |  /|
                  | / |
                  |/  |
    (-0.5, -0.5)  3---2  (0.5, -0.5)
  */
  const line = new Line3();
  const intersectPoint = new Vector3();
  const triangle1 = new Triangle( points[3], points[1], points[0] );
  const triangle2 = new Triangle( points[3], points[2], points[1] );
  const triangles = [triangle1, triangle2];
  for ( let i = 0; i < triangles.length; i++ ) {
    const triangle = triangles[i];
    const triangleVertices = [triangle.a, triangle.b, triangle.c];
    for ( let j = 0; j < 3; j++ ) {
      line.start = triangleVertices[j];
      line.end = triangleVertices[(j + 1) % 3];
      for ( let k = 0; k < frustum.planes.length; k++ ) {
        const plane = frustum.planes[k];
        const intersect = plane.intersectLine(line, intersectPoint);
        if (intersect) {
          const planes = [...frustum.planes];
          planes.splice( k, 1 ); // remove intersecting plane.
          const insideFrustum = planesContainPoint(planes, intersectPoint);
          if ( insideFrustum ) {
            return true;
          }
        }
      }
    }
  }

  // lastly test if triangles are completely outside of frustrum,
  // but the surface of the traingles covers the frustum.
  // therefore, a ray hit test on the triangle will tell us if this is the case.
  points.forEach(point => {
    point.applyMatrix4( camera.matrixWorld ); // reverting change made to points above.
  });
  const cameraDirection = new THREE.Vector3();
  camera.getWorldDirection(cameraDirection);
  const ray = new Ray(camera.position, cameraDirection);
  for ( let i = 0; i < triangles.length; i++ ) {
    const triangle = triangles[i];
    const intersect = ray.intersectTriangle(triangle.a, triangle.b, triangle.c, false, intersectPoint);
    if ( intersect ) {
      return true;
    }
  }
  return false;
};
EJCube.getFaceOnScreen;

EJCube.getFaceBoundingBox = ( faceIndex, matrixWorld, camera ) => {
  const boundingBox = new Box2();
  const boundingPoint = new Vector2();
  const frustum = new Frustum();
  frustum.setFromProjectionMatrix(camera.projectionMatrix);
  /*
     (-0.5, 0.5)  0----1  (0.5, 0.5)
                  |    |
                  |    |
    (-0.5, -0.5)  3----2  (0.5, -0.5)
  */
  const points = getFacePoints( faceIndex, matrixWorld );
  for ( let i = 0; i < points.length; i++ ) {
    const point = points[i];
    point.applyMatrix4( camera.matrixWorldInverse ); // taken out from point.project(camera)
    const px = frustum.planes[0].distanceToPoint( point ) < 0; // outside of frustum (right)
    const nx = frustum.planes[1].distanceToPoint( point ) < 0; // outside of frustum (left)
    const py = frustum.planes[2].distanceToPoint( point ) < 0; // outside of frustum (bottom)
    const ny = frustum.planes[3].distanceToPoint( point ) < 0; // outside of frustum (top)
    const pz = frustum.planes[4].distanceToPoint( point ) < 0; // outside of far clipping plane.
    const nz = frustum.planes[5].distanceToPoint( point ) < 0; // outside of near clipping plane.
    if ( nz || pz ) {
      // one of the points is beyond the near or far clipping planes.
      // this will retusult in an incorrect projection of the point to screen.
      // abort bounding box and return null.
      return null;
    }
    point.applyMatrix4( camera.projectionMatrix ); // taken out from point.project(camera)
    if ( point.z < -1.0 || point.z > 1.0 ) {
      // The projection matrix transforms 3D coordinates into normalized device coordinates (NDC),
      // where the x and y coordinates are mapped to the screen space, and the z coordinate represents the depth information.
      // However, the depth value (z) in NDC is usually normalized between -1 and 1.
      // If a point's z value falls outside this range, it means the point is either behind the camera or too far in front of it.
      // In such cases, the projected point will appear incorrect or outside the visible screen area.
      console.log( `point.z out of range?` );
      // return null;
    }
    boundingPoint.x = (point.x + 1) * 0.5;
    boundingPoint.y = -(point.y - 1) * 0.5;
    if ( px ) {
      boundingPoint.x = 1;
    }
    if ( nx ) {
      boundingPoint.x = 0;
    }
    if ( py ) {
      boundingPoint.y = 1;
    }
    if ( ny ) {
      boundingPoint.y = 0;
    }
    boundingBox.expandByPoint( boundingPoint );
  }
  return boundingBox;
};
EJCube.getFaceBoundingBox;

EJCube.getFaceDistanceToCamera = ( faceIndex, matrixWorld, camera ) => {
  /*
     (-0.5, 0.5)  0---1  (0.5, 0.5)
                  |  /|
                  | / |
                  |/  |
    (-0.5, -0.5)  3---2  (0.5, -0.5)
  */
  const points = getFacePoints( faceIndex, matrixWorld );
  const triangle1 = new Triangle( points[3], points[1], points[0] );
  const triangle2 = new Triangle( points[3], points[2], points[1] );
  const triangles = [triangle1, triangle2];
  const target = new Vector3();
  let distanceClosest = 0;
  triangles.forEach( (triangle, i) => {
    triangle.closestPointToPoint( camera.position, target );
    const distance = camera.position.distanceTo( target );
    if ( i === 0 ) {
      distanceClosest = distance;
    } else {
      distanceClosest = Math.min( distanceClosest, distance );
    }
  });
  return distanceClosest;
};
EJCube.getFaceDistanceToCamera;

EJCube.initCubeGeometry = () => {
  // https://r105.threejsfundamentals.org/threejs/lessons/threejs-custom-geometry.html
  // https://threejs.org/docs/#api/en/core/BufferGeometry.addAttribute

  /*
       6----7
      /|   /|
    2----3 |
    | |  | |
    | 4--|-5
    |/   |/
    0----1
  */

  const geometry = new BufferGeometry();
  geometry.name = 'EJCubeGeometry';

  const verts = [];
  verts.push(-1, -1, 1); // 0
  verts.push(1, -1, 1); // 1
  verts.push(-1, 1, 1); // 2
  verts.push(1, 1, 1); // 3
  verts.push(-1, -1, -1); // 4
  verts.push(1, -1, -1); // 5
  verts.push(-1, 1, -1); // 6
  verts.push(1, 1, -1); // 7
  for (let i = 0; i < verts.length; i++) {
    verts[i] *= 0.5; // make unit cube
  }

  const vertColors = [];
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);
  vertColors.push(1.0, 0.0, 1.0, 0.5);

  const faceIndices = [];
  // front
  faceIndices.push(0, 3, 2);
  faceIndices.push(0, 1, 3);
  // right
  faceIndices.push(1, 7, 3);
  faceIndices.push(1, 5, 7);
  // back
  faceIndices.push(5, 6, 7);
  faceIndices.push(5, 4, 6);
  // left
  faceIndices.push(4, 2, 6);
  faceIndices.push(4, 0, 2);
  // top
  faceIndices.push(2, 7, 6);
  faceIndices.push(2, 3, 7);
  // bottom
  faceIndices.push(4, 1, 0);
  faceIndices.push(4, 5, 1);

  const vertices = [];
  const colors = [];

  for (let i = 0; i < faceIndices.length; i++) {
    const fi = faceIndices[i];
    const vi = fi * 3;
    vertices.push(verts[vi + 0]);
    vertices.push(verts[vi + 1]);
    vertices.push(verts[vi + 2]);
    const ci = fi * 4;
    colors.push(vertColors[ci + 0]);
    colors.push(vertColors[ci + 1]);
    colors.push(vertColors[ci + 2]);
    colors.push(vertColors[ci + 3]);
  }

  const uvs = [];
  for (let i = 0; i < 6; i++) {
    uvs.push(0, 0);
    uvs.push(1, 1);
    uvs.push(0, 1);
    uvs.push(0, 0);
    uvs.push(1, 0);
    uvs.push(1, 1);
  }

  const normals = [];
  const faceNormals = getFaceNormals();
  for (let i = 0; i < faceNormals.length; i++) {
    const faceNormal = faceNormals[i];
    for (let i = 0; i < 6; i++) {
      normals.push(faceNormal.x, faceNormal.y, faceNormal.z);
    }
  }

  const centres = [];
  const faceCentres = getFaceCentres();
  for (let i = 0; i < faceCentres.length; i++) {
    const faceCentre = faceCentres[i];
    for (let i = 0; i < 6; i++) {
      centres.push(faceCentre.x, faceCentre.y, faceCentre.z);
    }
  }

  const faces = [];
  for (let i = 0; i < 6; i++) { // for each face.
    for (let j = 0; j < 6; j++) { // 2 trianges, 6 points per face.
      faces.push(i);
    }
  }

  geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
  geometry.setAttribute('centre', new Float32BufferAttribute(centres, 3));
  geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 4));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('face', new Float32BufferAttribute(faces, 1));

  return geometry;
};
EJCube.initCubeGeometry;

// ---------------------------------------------------------------- EJCubeBase
const EJCubeStyleNone = 'None';
const EJCubeStylePlain = 'Plain';
const EJCubeStyleGlass = 'Glass';
const EJCubeStyleGlass3 = 'Glass3';


/**
 *
 */
class EJCubeBase {
  /**
   * @param {Object3D} container
   * @param {any} gui
   */
  constructor(container, gui) {
    this.style = EJCubeStyleNone;
    this.container = container;
    this.cubeBack = null;
    this.cubeFront = null;
    this.cubeToCameraDist = 100;
    this.cubeToCameraDistNorm = 1.0;
    this.cubeToCameraThreshold = 0.05;
    this.raycaster = new Raycaster();

    this.gui = gui;
    if (this.gui) {
      this.guiFolder = this.gui.addFolder('Cube Settings');
    }
  }

  setConfig( value ) {
    //
  }

  updateProps(texture, resolution) {
    //
  }

  updateMasterOpacity( value ) {
    // each cube must extend and handle this differently,
    // because opacity is usually a value in shader and all cubes
    // use different shaders.
  }

  /**
   * @param {Camera} camera
   */
  update(camera) {
    this.cubeToCameraDist = 100;

    this.raycaster.setFromCamera(new Vector2(0, 0), camera);

    const intersectsFront = this.raycaster.intersectObject(this.cubeFront);
    if (intersectsFront.length > 0) {
      const intersect = intersectsFront[0];
      this.cubeToCameraDist = intersect.distance;
    }

    this.raycaster.ray.direction.multiply(new Vector3(-1, -1, -1));

    const intersectsBack = this.raycaster.intersectObject(this.cubeBack);
    if (intersectsBack.length > 0) {
      const intersect = intersectsBack[0];
      this.cubeToCameraDist = -intersect.distance;
    }

    this.cubeToCameraDistNorm = MathUtils.mapLinear(this.cubeToCameraDist, -this.cubeToCameraThreshold, this.cubeToCameraThreshold, -1.0, 1.0);
    this.cubeToCameraDistNorm = MathUtils.clamp(this.cubeToCameraDistNorm, -1.0, 1.0);
  }

  dispose() {
    if (this.container) {
      EJUtils.disposeRecursive(this.container);
    }
    if (this.guiFolder) {
      this.guiFolder.destroy();
      this.guiFolder = null;
    }
  }
}

/* eslint-disable max-len */

/**
 * @return {ShaderMaterial}
 */
function initCubeMaterial$2() {
  const material = new RawShaderMaterial({
    uniforms: {
      backFace: { value: 1 },
      borderOn: { value: 1 },
      borderColor: { value: new Vector4(1, 1, 1, 0.25) },
      borderWidth: { value: 0.0025 },
      borderFeather: { value: 0.5 },
      textureOn: { value: 1 },
      texture: { type: 't', value: null },
      resolution: { value: new Vector2(0, 0) },
      masterOpacity: { value: 1.0 },
    },
    vertexShader: `
      precision highp float;
      precision highp int;

      uniform mat4 modelMatrix;
      uniform mat4 modelViewMatrix;
      uniform mat4 projectionMatrix;

      attribute vec3 position;
      attribute vec4 color;
      attribute vec2 uv;
      
      varying vec2 vUv;
      
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
    `,
    // grid shader.
    // http://madebyevan.com/shaders/grid/
    //
    fragmentShader: `
      precision highp float;
      precision highp int;
      
      uniform int backFace;
      uniform int borderOn;
      uniform vec4 borderColor;
      uniform float borderWidth;
      uniform float borderFeather;
      uniform int textureOn;
      uniform sampler2D texture;
      uniform vec2 resolution;
      uniform float masterOpacity;
      
      varying vec2 vUv;

      vec4 alphaBlend( vec4 src, vec4 dst ) {
        float final_alpha = src.a + dst.a * (1.0 - src.a);
        if( final_alpha == 0.0 ) {
          return vec4( 0.0, 0.0, 0.0, 0.0 );
        }
        return vec4( (src.rgb * src.a + dst.rgb * dst.a * (1.0 - src.a)) / final_alpha, final_alpha);
      }

      float border(vec2 uv, float strokeWidth, float feather) {
        vec2 borderBottomLeft = smoothstep(vec2(strokeWidth * feather), vec2(strokeWidth), uv);
        vec2 borderTopRight = smoothstep(vec2(strokeWidth * feather), vec2(strokeWidth), 1.0 - uv);
        return 1.0 - borderBottomLeft.x * borderBottomLeft.y * borderTopRight.x * borderTopRight.y;
      }

      vec4 LinearTosRGB( vec4 value ) {
        return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
      }      
      
      void main()  {
        vec4 colorTex = vec4(0.0, 0.0, 0.0, 0.0);
        vec4 colorBorder = vec4(0.0, 0.0, 0.0, 0.0);
        
        if(textureOn == 1) {
          vec2 st = gl_FragCoord.xy / resolution;
          colorTex = texture2D(texture, st);
        }

        if(borderOn == 1 && borderWidth > 0.0 && borderColor.a > 0.0) {
          float line = border(vUv, borderWidth, 1.0 - borderFeather);
          colorBorder = vec4(borderColor.xyz, borderColor.a * line);
        }

        vec4 colorFinal = vec4(0.0, 0.0, 0.0, 0.0);
        if( backFace == 0 ) {
          colorFinal = alphaBlend(colorBorder, colorTex); // border first, then blend content on top.
        } else {
          colorFinal = alphaBlend(colorTex, colorBorder); // content first, then blend border on top.
        }

        colorFinal = LinearTosRGB( colorFinal );
        colorFinal.a *= masterOpacity;

        gl_FragColor = colorFinal;
      }      
    `,
    side: DoubleSide,
    transparent: true,
  });
  return material;
}

// ---------------------------------------------------------------- EJCube1
class EJCube1 extends EJCubeBase {
  constructor(container, gui) {
    super(container, gui);
    this.style = EJCubeStylePlain;

    this.geometry = EJCube.initCubeGeometry();
    this.materialBack = initCubeMaterial$2();
    this.materialFront = initCubeMaterial$2();

    this.cubeBack = new Mesh(this.geometry, this.materialBack);
    this.cubeBack.name = 'cubeBack';
    this.materialBack.side = BackSide;
    this.materialBack.uniforms.backFace.value = 1;
    this.materialBack.uniforms.textureOn.value = 1;
    this.materialBack.uniformsNeedUpdate = true;

    this.cubeFront = new Mesh(this.geometry, this.materialFront);
    this.cubeFront.name = 'cubeFront';
    this.materialFront.side = FrontSide;
    this.materialFront.uniforms.backFace.value = 0;
    this.materialFront.uniforms.textureOn.value = 0;
    this.materialFront.uniformsNeedUpdate = true;

    this.container.add(this.cubeBack);
    this.container.add(this.cubeFront);

    this.config = {
      masterOpacity: 1.0,
      borderOn: true,
      borderWidth: 0.0025,
      borderFeather: 0.5,
      borderColor: new Color(0xffffff),
      borderAlpha: 0.25,
    };

    if (this.guiFolder) {
      const masterOpacityChanged = value => {
        this.updateMasterOpacity( value );
      };
      const borderOnChanged = value => {
        this.setConfig( { borderOn: value } );
      };
      const borderWidthChanged = value => {
        this.setConfig( { borderWidth: value } );
      };
      const borderFeatherChanged = value => {
        this.setConfig( { borderFeather: value } );
      };
      const borderColorChanged = value => {
        this.setConfig( { borderColor: value } );
      };
      const borderAlphaChanged = value => {
        this.setConfig( { borderAlpha: value } );
      };
      this.guiFolder.add(this.config, 'masterOpacity', 0.0, 1.0).name('Master Opacity').onChange( masterOpacityChanged.bind(this) );
      this.guiFolder.add(this.config, 'borderOn').name('Border On').onChange( borderOnChanged.bind(this) );
      this.guiFolder.add(this.config, 'borderWidth', 0.0, 0.1).name('Border Width').onChange( borderWidthChanged.bind(this) );
      this.guiFolder.add(this.config, 'borderFeather', 0.0, 1.0).name('Border Feather').onChange( borderFeatherChanged.bind(this) );
      this.guiFolder.addColor(this.config, 'borderColor').name('Border Color').onChange( borderColorChanged.bind(this) );
      this.guiFolder.add(this.config, 'borderAlpha', 0.0, 1.0).name('Border Alpha').onChange( borderAlphaChanged.bind(this) );
    }
  }

  setConfig( config ) {
    if ( config ) {
      if ( config.borderOn !== undefined ) {
        this.materialBack.uniforms.borderOn.value = config.borderOn ? 1 : 0;
        this.materialBack.uniformsNeedUpdate = true;
        this.materialFront.uniforms.borderOn.value = config.borderOn ? 1 : 0;
        this.materialFront.uniformsNeedUpdate = true;
      }
      if ( config.borderWidth !== undefined ) {
        this.materialBack.uniforms.borderWidth.value = config.borderWidth;
        this.materialBack.uniformsNeedUpdate = true;
        this.materialFront.uniforms.borderWidth.value = config.borderWidth;
        this.materialFront.uniformsNeedUpdate = true;
      }
      if ( config.borderFeather !== undefined ) {
        this.materialBack.uniforms.borderFeather.value = config.borderFeather;
        this.materialBack.uniformsNeedUpdate = true;
        this.materialFront.uniforms.borderFeather.value = config.borderFeather;
        this.materialFront.uniformsNeedUpdate = true;
      }
      if ( config.borderColor !== undefined ) {
        this.materialBack.uniforms.borderColor.value.x = config.borderColor.r;
        this.materialBack.uniforms.borderColor.value.y = config.borderColor.g;
        this.materialBack.uniforms.borderColor.value.z = config.borderColor.b;
        this.materialBack.uniformsNeedUpdate = true;
        this.materialFront.uniforms.borderColor.value.x = config.borderColor.r;
        this.materialFront.uniforms.borderColor.value.y = config.borderColor.g;
        this.materialFront.uniforms.borderColor.value.z = config.borderColor.b;
        this.materialFront.uniformsNeedUpdate = true;
      }
      if ( config.borderAlpha !== undefined ) {
        this.materialBack.uniforms.borderColor.value.w = config.borderAlpha;
        this.materialBack.uniformsNeedUpdate = true;
        this.materialFront.uniforms.borderColor.value.w = config.borderAlpha;
        this.materialFront.uniformsNeedUpdate = true;
      }
    }
  }

  update(camera) {
    super.update(camera);
  }

  updateProps(texture, resolution) {
    super.updateProps(texture, resolution);

    let materialBackUpdate = false;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.texture.value !== texture;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialBackUpdate ) {
      this.materialBack.uniforms.texture.value = texture;
      this.materialBack.uniforms.resolution.value = resolution;
      this.materialBack.uniformsNeedUpdate = true;
    }

    let materialFrontUpdate = false;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.texture.value !== texture;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialFrontUpdate ) {
      this.materialFront.uniforms.texture.value = texture;
      this.materialFront.uniforms.resolution.value = resolution;
      this.materialFront.uniformsNeedUpdate = true;
    }
  }

  updateMasterOpacity( value ) {
    if ( this.materialBack.uniforms.masterOpacity.value != value ) {
      this.materialBack.uniforms.masterOpacity.value = value;
      this.materialBack.uniformsNeedUpdate = true;
    }
    if ( this.materialFront.uniforms.masterOpacity.value != value ) {
      this.materialFront.uniforms.masterOpacity.value = value;
      this.materialFront.uniformsNeedUpdate = true;
    }
  }
}

/* eslint-disable max-len */

function initCubeMaterial$1() {
  const material = new RawShaderMaterial({
    name: 'EJCube1Material',
    uniforms: {
      mode: { value: 0 },
      time: { value: 1.0 },
      resolution: { value: new Vector2(0, 0) },
      texture: { type: 't', value: null },
      tintAlpha: { value: 0.4 },
      cubeToCameraDistNorm: { value: 1.0 },
    },
    vertexShader: `
      precision highp float;
      precision highp int;

      uniform mat4 modelViewMatrix; // optional
      uniform mat4 projectionMatrix; // optional

      attribute vec3 position;
      attribute vec3 centre;
      attribute vec3 normal;
      attribute vec4 color;
      attribute vec2 uv;

      varying vec3 vPosition;
      varying vec4 vPositionWorld;
      varying vec3 vCentre;
      varying vec4 vCentreWorld;
      varying vec3 vNormal;
      varying vec4 vColor;
      varying vec2 vUv;

      void main() {

        vPosition = position;
        vPositionWorld = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        vCentre = centre;
        vCentreWorld = projectionMatrix * modelViewMatrix * vec4( centre, 1.0 );
        vNormal = normal;
        vColor = color;
        vUv = uv;

        gl_Position = vPositionWorld;
      }
    `,
    fragmentShader: `
      precision highp float;
      precision highp int;

      uniform int mode;
      uniform float time;
      uniform vec2 resolution;
      uniform sampler2D texture;
      uniform float tintAlpha;
      uniform float cubeToCameraDistNorm;

      varying vec3 vPosition;
      varying vec4 vPositionWorld;
      varying vec3 vCentre;
      varying vec4 vCentreWorld;
      varying vec3 vNormal;
      varying vec4 vColor;
      varying vec2 vUv;

      const float PI2 = 6.283185307179586;

      float map( float value, float inputMin, float inputMax, float outputMin, float outputMax ) {
        if( abs(inputMin - inputMax) < 0.00001 ) {
          return outputMin;
        } else {
          float outVal = ((value - inputMin) / (inputMax - inputMin) * (outputMax - outputMin) + outputMin);
          if(outputMax < outputMin){
            if( outVal < outputMax )outVal = outputMax;
            else if( outVal > outputMin )outVal = outputMin;
          } else {
            if( outVal > outputMax )outVal = outputMax;
            else if( outVal < outputMin )outVal = outputMin;
          }
          return outVal;
        }
      }

      vec4 alphaBlend( vec4 src, vec4 dst ) {
        float final_alpha = src.a + dst.a * (1.0 - src.a);
        if( final_alpha == 0.0 ) {
          return vec4( 0.0, 0.0, 0.0, 0.0 );
        }
        return vec4( (src.rgb * src.a + dst.rgb * dst.a * (1.0 - src.a)) / final_alpha, final_alpha);
      }

      float borders(vec2 uv, float strokeWidth) {
        vec2 borderBottomLeft = smoothstep(vec2(0.0), vec2(strokeWidth), uv);
        vec2 borderTopRight = smoothstep(vec2(0.0), vec2(strokeWidth), 1.0 - uv);
        return 1.0 - borderBottomLeft.x * borderBottomLeft.y * borderTopRight.x * borderTopRight.y;
      }

      vec4 warp( vec2 st ) {
        vec2 toCenter = vCentreWorld.xy - vPositionWorld.xy;
        float direction = (atan(toCenter.y, toCenter.x) / PI2) + 0.5;
        float length = borders(vUv, 0.028) + borders(vUv, 0.06) * 0.3;
        if( false ) { // debug.
          return vec4(direction, length, 0.0, 1.0);
        }

        vec2 dir = vec2(cos(direction * PI2), sin(direction * PI2));

        vec2 stWarp = st;
        stWarp.x += (length * 0.07) * dir.x;
        stWarp.y += (length * 0.07) * dir.y;

        return texture2D(texture, stWarp);
      }

      void main()  {

        vec2 st = gl_FragCoord.xy / resolution;
        vec4 color = vec4( 0.0, 0.0, 0.0, 0.0 );

        vec4 colorBorder = vec4( borders(vUv, 0.02) );
        if( colorBorder.a > 0.0 ) {
          colorBorder.rgb /= colorBorder.a; // premultiply alpha fix for dark shadows.
        }

        float colorTexAlphaMin = 0.5;
        float frostAlphaMax = 0.6;

        if( mode == 0 ) { // cube interior / back facing.

          vec4 colorTint = vec4( 0.0, 0.0, 0.0, tintAlpha );
          vec4 colorBorderFaded = vec4( colorBorder.rgb, colorBorder.a * 0.6 );
          
          color = alphaBlend( colorBorderFaded, colorTint );

          if( cubeToCameraDistNorm < 0.0 ) { // below 0.0, meams we're inside the cube.
            
            vec4 colorTex = texture2D(texture, st);
            float colorTexAlpha = map( cubeToCameraDistNorm, 0.0, -0.5, colorTexAlphaMin, 1.0 );
            colorTex.a *= colorTexAlpha;

            float frostAlpha = map( cubeToCameraDistNorm, 0.0, -1.0, frostAlphaMax, 0.0 );
            vec4 frostCol = vec4(1.0, 1.0, 1.0, frostAlpha );

            color = alphaBlend( colorTex, color );
            color = alphaBlend( frostCol, color );
          }

        } else { // cube exterior / front facing.

          if( cubeToCameraDistNorm < 0.0 ) {
            discard; // below 0.0, meams we're inside the cube.
          }

          vec4 colorTex = warp( st );
          float colorTexAlpha = map( cubeToCameraDistNorm, 1.0, 0.5, 1.0, colorTexAlphaMin );
          colorTex.a *= colorTexAlpha;

          float frostAlpha = map( cubeToCameraDistNorm, 1.0, 0.0, 0.0, frostAlphaMax );
          vec4 frostCol = vec4(1.0, 1.0, 1.0, frostAlpha );

          color = colorTex;
          color = alphaBlend( frostCol, color );
          color = alphaBlend( colorBorder, color );
        }

        gl_FragColor = color;
      }
    `,
    side: DoubleSide,
    transparent: true,
  });

  return material;
}

// ---------------------------------------------------------------- EJCube2
class EJCube2 extends EJCubeBase {
  constructor(container, gui) {
    super(container, gui);
    this.style = EJCubeStyleGlass;

    this.geometry = EJCube.initCubeGeometry();
    this.materialBack = initCubeMaterial$1();
    this.materialFront = initCubeMaterial$1();

    this.cubeBack = new Mesh(this.geometry, this.materialBack);
    this.cubeBack.name = 'cubeBack';
    this.cubeBack.material.side = BackSide;
    this.cubeBack.material.uniforms.mode.value = 0;
    this.cubeBack.material.uniformsNeedUpdate = true;

    this.cubeFront = new Mesh(this.geometry, this.materialFront);
    this.cubeFront.name = 'cubeFront';
    this.cubeFront.material.side = FrontSide;
    this.cubeFront.material.uniforms.mode.value = 1;
    this.cubeFront.material.uniformsNeedUpdate = true;

    this.container.add(this.cubeBack);
    this.container.add(this.cubeFront);

    if (this.guiFolder) {
      this.guiFolder.add(this.materialBack.uniforms.tintAlpha, 'value', 0.0, 1.0).name('Tint Alpha').onChange(value => {
        this.materialBack.uniforms.tintAlpha.value = value;
        this.materialBack.uniformsNeedUpdate = true;
      });
      this.guiFolder.add(this, 'cubeToCameraThreshold', 0.0, 1.0).name('Cube Threshold').onChange(value => {
        this.cubeToCameraThreshold = value;
      });
    }
  }

  update(camera) {
    super.update(camera);

    this.materialBack.uniforms.cubeToCameraDistNorm.value = this.cubeToCameraDistNorm;
    this.materialBack.uniformsNeedUpdate = true;

    this.materialFront.uniforms.cubeToCameraDistNorm.value = this.cubeToCameraDistNorm;
    this.materialFront.uniformsNeedUpdate = true;
  }

  updateProps(texture, resolution) {
    super.updateProps(texture, resolution);

    let materialBackUpdate = false;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.texture.value !== texture;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialBackUpdate ) {
      this.materialBack.uniforms.texture.value = texture;
      this.materialBack.uniforms.resolution.value = resolution;
      this.materialBack.uniformsNeedUpdate = true;
    }

    let materialFrontUpdate = false;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.texture.value !== texture;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialFrontUpdate ) {
      this.materialFront.uniforms.texture.value = texture;
      this.materialFront.uniforms.resolution.value = resolution;
      this.materialFront.uniformsNeedUpdate = true;
    }
  }

  dispose() {
    super.dispose();
  }
}

const shaderVert = "#version 300 es\n//3.00 we need for normal map calc\n\nprecision highp float;\nprecision highp int;\n\n//built in\n//https://threejs.org/docs/#api/en/renderers/webgl/WebGLProgram\nuniform mat4 modelViewMatrix; \nuniform mat4 projectionMatrix; \nuniform vec3 cameraPosition;  \nuniform mat4 viewMatrix;\nuniform mat4 modelMatrix;\n\n\nin vec3 position;\nin vec3 centre;\nin vec3 normal;\nin vec3 tangent;\nin vec4 color;\nin vec2 uv;\n\n\nout vec3 vPosWorld; //correct world space \nout vec3 vNormalWorld;\n// out vec3 vTangentWorld;\n// out vec3 vBitangentWorld;\nout vec3 vViewDir;\nout vec3 vViewPosition;\n// out vec4 vColor;\nout vec2 vUv;\n\n//old\nout vec3 vPosition;\nout vec4 vPositionWorld;\nout vec3 vCentre;\nout vec4 vCentreWorld;\nout vec3 vNormal;\n\n\n//from THREEjs examples\nvec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {\n  return normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );\n}\n\nvoid main() {\n\n  vPosWorld =  ( modelMatrix * vec4( position, 1. ) ).xyz;\n  vNormalWorld = inverseTransformDirection( normal, modelMatrix );\n  vViewDir = normalize(vPosWorld - cameraPosition); //?\n\n  /*\n  vTangentWorld = normalize( (vec4( tangent, 0.0 ) * modelMatrix ).xyz );\n  vBitangentWorld = normalize( cross( vNormalWorld, vTangentWorld ) );\n  */\n\n  //these we need to normal map calc\n  vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );\n  vViewPosition = - mvPosition.xyz;\n\n  vUv = uv;\n\n  //old vals\n  //TODO DEPRECATE\n  vPosition = position;\n  vPositionWorld = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );\n  vCentre = centre;\n  vCentreWorld = projectionMatrix * modelViewMatrix * vec4( centre, 1.0 );\n  vNormal = normal;\n  // vColor = color;\n\n  gl_Position = vPositionWorld;\n}";

const shaderFrag = "#version 300 es\n\nprecision highp float;\nprecision highp int;\n\n#define PI 3.14159\n#define PI2 6.283185307179586\n\n//built in \n//https://threejs.org/docs/#api/en/renderers/webgl/WebGLProgram\nuniform vec3 cameraPosition;  \nuniform mat4 viewMatrix;\nuniform mat4 modelMatrix;\nuniform mat4 projectionMatrix;\n\n\n//from vert\nin vec3 vPosWorld;\nin vec3 vNormalWorld;\nin vec3 vViewDir;\nin vec3 vViewPosition;\nin vec2 vUv; //surface uvs\n// in vec4 vColor;\n// in vec3 vTangentWorld;\n// in vec3 vBitangentWorld;\n\n//old\nin vec3 vPosition;\nin vec4 vPositionWorld;\nin vec3 vCentre;\nin vec4 vCentreWorld;\nin vec3 vNormal;\n\nout vec4 fragColor;\n\n\n\nuniform int mode;  //rename to faceDir \nuniform float time;\nuniform vec2 resolution;\nuniform sampler2D texBack; //todo rename to transmissionSamplerMap or back texture \nuniform float cubeToCameraDistNorm;\n\nuniform float tintAlpha;\nuniform vec3 tintColor; //rename to tintColor\n// uniform float camFOV;\n\nuniform sampler2D texHDRI; //equirectangular HDRI\nuniform float texEnvExposure;\n\nconst float normalScale = .02;\nuniform sampler2D texNormal; \n\n\n/*\nfloat map( float value, float inputMin, float inputMax, float outputMin, float outputMax ) {\n  if( abs(inputMin - inputMax) < .00001 ) {\n    return outputMin;\n  } else {\n    float outVal = ((value - inputMin) / (inputMax - inputMin) * (outputMax - outputMin) + outputMin);\n    if(outputMax < outputMin){\n      if( outVal < outputMax )outVal = outputMax;\n      else if( outVal > outputMin )outVal = outputMin;\n    } else {\n      if( outVal > outputMax )outVal = outputMax;\n      else if( outVal < outputMin )outVal = outputMin;\n    }\n    return outVal;\n  }\n}\n\nvec4 alphaBlend( vec4 src, vec4 dst ) {\n  float final_alpha = src.a + dst.a * (1.0 - src.a);\n  if(final_alpha == .0) {\n    return vec4(.0,.0,.0,.0);\n  }\n  return vec4( (src.rgb * src.a + dst.rgb * dst.a * (1.0 - src.a)) / final_alpha, final_alpha);\n}\n\n\nfloat borders(vec2 uv, float strokeWidth) {\n  vec2 borderBottomLeft = smoothstep(vec2(0.0), vec2(strokeWidth), uv);\n  vec2 borderTopRight = smoothstep(vec2(0.0), vec2(strokeWidth), 1.0 - uv);\n  return 1.0 - borderBottomLeft.x * borderBottomLeft.y * borderTopRight.x * borderTopRight.y;\n}\n\nvec4 warp( vec2 st ) {\n  vec2 toCenter = vCentreWorld.xy - vPositionWorld.xy;\n  float direction = (atan(toCenter.y, toCenter.x) / PI2) + 0.5;\n  float length = borders(vUv, 0.028) + borders(vUv, 0.06) * 0.3;\n  if( false ) { // debug.\n    return vec4(direction, length, 0.0, 1.0);\n  }\n\n  vec2 dir = vec2(cos(direction * PI2), sin(direction * PI2));\n\n  vec2 stWarp = st;\n  stWarp.x += (length * 0.07) * dir.x;\n  stWarp.y += (length * 0.07) * dir.y;\n\n  return texture(texBack, stWarp);\n}\n*/\n/*\n// with pre-calc tangents\nvec3 getDetailNormal2(vec3 n){ \n\n  float normalScale = 0.02;\n  vec3 mapN = texture(texNormal, vUv ).xyz * 2. - 1.;\n  mapN.xy *= normalScale;\n\n\n  // #ifdef FLIP_SIDED\n  //   transformedTangent = - transformedTangent;\n  // #endif\n\n\n  vec3 tangent = normalize( vTangentWorld );\n  vec3 bitangent = normalize( vBitangentWorld );\n\n  // #ifdef DOUBLE_SIDED\n  //   tangent = tangent * faceDirection;\n  //   bitangent = bitangent * faceDirection;\n  // #endif\n  // #if defined( TANGENTSPACE_NORMALMAP ) || defined( USE_CLEARCOAT_NORMALMAP )\n\n  mat3 vTBN = mat3( vTangentWorld, vBitangentWorld, vNormalWorld );\n  vec3 nDetailed = normalize( vTBN * mapN );\n\n\n\n  // float faceDirection = gl_FrontFacing ? 1. : - 1.;\n  // vec3 nDetailed = applyNormalMap( n, mapN, faceDirection ); \n  return nDetailed;\n\n}\n*/\n\n\n\n//https://stackoverflow.com/questions/56625730/does-blending-work-with-the-glsl-mix-function\nvec4 blend(vec4 src, vec4 dst, float alpha){ return src*alpha + dst*(1.-alpha); }\n\n// vec3 bms(vec3 a, vec3 b){ return 1.- (1.-a)*(1.-b); }\n// float bms(float a, float b){ return 1.- (1.-a)*(1.-b); }\n\n\n//Equirectangular HDRI\n//https://www.shadertoy.com/view/4lK3DK\nvec3 getHDRI(vec3 rd){\n  vec2 uv = vec2(atan(rd.z, rd.x) + PI, acos(-rd.y)) / vec2(2. * PI, PI); //TODO PI2\n  vec3 col = texture(texHDRI, uv).rgb;\n  //TODO: on THREEjs side\n  col = pow(col, vec3(2.2)); //sRGB -> Linear, \n  col *= texEnvExposure;\n  return col;\n}\n\n//Note: it will be faster with precomputed tangents. bi tangents cud be calculated from t x n\n//from normalmap_pars_fragment.glsl.js\n// Normal Mapping Without Precomputed Tangents\n// http://www.thetenthplanet.de/archives/1180\nvec3 perturbNormal2Arb( vec3 eye_pos, vec3 surf_norm, vec3 mapN, float faceDirection ) {\n\n  vec3 q0 = dFdx( eye_pos.xyz );\n  vec3 q1 = dFdy( eye_pos.xyz );\n  vec2 st0 = dFdx( vUv.st );\n  vec2 st1 = dFdy( vUv.st );\n\n  vec3 N = surf_norm; // normalized\n\n  vec3 q1perp = cross( q1, N );\n  vec3 q0perp = cross( N, q0 );\n\n  vec3 T = q1perp * st0.x + q0perp * st1.x;\n  vec3 B = q1perp * st0.y + q0perp * st1.y;\n\n  float det = max( dot( T, T ), dot( B, B ) );\n  float scale = ( det == 0.0 ) ? 0.0 : faceDirection * inversesqrt( det );\n\n  return normalize( T * ( mapN.x * scale ) + B * ( mapN.y * scale ) + N * mapN.z );\n\n}\n\nvec3 getDetailNormal(vec3 n){ \n\n  // float normalScale = 0.02;\n  vec3 mapN = texture(texNormal, vUv).xyz * 2. - 1.;\n  mapN.xy *= normalScale;\n\n  //TODO check with main normal - coz we flip it also\n  float faceDirection = gl_FrontFacing ? 1. : - 1.;\n\n  vec3 normal = perturbNormal2Arb( - vViewPosition, n, mapN, faceDirection );\n  return normal;\n}\n\n\n//back texture refration\n//from transmission_pars_fragment.glsl.js\nvec4 transmission(vec3 n){\n\n  vec3 pos = vPosWorld;\n  vec3 v = -vViewDir;\n  float ior = 2.;  //we use different ior for refration coz it looks better\n  float thickness = .05;\n\n  //face dir==back face\n  // if(mode==0) ior = 1./ior; //glass to air\n\n  // Direction of refracted light.\n  vec3 refractionVector = refract( - v, normalize( n ), 1.0 / ior );\n\n  // Compute rotation-independant scaling of the model matrix.\n  vec3 modelScale;\n  modelScale.x = length( vec3( modelMatrix[ 0 ].xyz ) );\n  modelScale.y = length( vec3( modelMatrix[ 1 ].xyz ) );\n  modelScale.z = length( vec3( modelMatrix[ 2 ].xyz ) );\n\n  // The thickness is specified in local space.\n  vec3 transmissionRay =  normalize( refractionVector ) * thickness * modelScale;\n\n  vec3 refractedRayExit = pos + transmissionRay;\n\n  // Project refracted vector on the framebuffer, \n  // while mapping to normalized device coordinates.\n  vec4 ndcPos = projectionMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );\n  vec2 refractionCoords = ndcPos.xy / ndcPos.w;\n  refractionCoords += 1.0;\n  refractionCoords /= 2.0;\n\n  vec4 col = texture(texBack, refractionCoords.xy).xyzw;\n  return col;\n\n}\n\n\n//surface shading \n\n//fresnel-schlick\nconst float EPS = 1e-3;\nfloat dot_c(vec3 a, vec3 b){ return max(dot(a, b), EPS); } //clamped dot with min eps val\nfloat fre(vec3 n, vec3 rayDir, float ior){\n  float f0 = pow(ior - 1., 2.) / pow(ior + 1., 2.); \n  float cosTheta = dot_c(n, -rayDir);\n  float f = f0 + (1.-f0) * pow(1.-cosTheta, 5.);\n  return f;\n}\n\n\nvec4 shadeFront(vec3 p, vec3 n, vec3 rayDir){ \n  \n  vec4 col = vec4(0.,0.,0.,0.); \n  vec4 ambientCol = transmission(n); //back texture refraction\n\n  vec3 reflectedCol = getHDRI(reflect(rayDir, n));\n  \n  //this one is for fresnel. we have another one for refraction. coz it looks better\n  float ior = 1.2;\n  float f = fre(n, rayDir, ior);\n\n  vec4 colTint = vec4(tintColor, tintAlpha);\n\n  col = colTint;\n  col = blend(ambientCol, col, ambientCol.a );   //inner material \n  col = blend(vec4(reflectedCol,1.), col, f ); //surface material \n\n  // col = mix(ambientCol, vec4(reflectedCol,1.), vec4(f.x) );\n\n  return col;\n}\n\n\nvec4 shadeBack(vec3 p, vec3 n, vec3 rayDir){ \n\n  vec4 col = vec4(0.,0.,0.,0.);\n  // vec4 colTint = vec4(tintColor, tintAlpha);\n\n  vec3 reflectedCol = getHDRI(reflect(rayDir, n));\n  \n  float ior = 1.2;\n  float f = fre(n, rayDir,ior);\n\n  col = blend(vec4(reflectedCol, 1.), col, f );\n\n  return col;\n}\n\n\n\nvoid main()  {\n\n  vec2 uv = gl_FragCoord.xy / resolution; //screen uvs\n  // bool faceDir = gl_FrontFacing;// ? 1. : - 1.;\n  vec4 col = vec4(0.,0.,0.,0.);\n\n  /*\n  vec4 colBorder = vec4( borders(vUv, 0.02) );\n  if( colBorder.a > 0.0 ) {\n    colBorder.rgb /= colBorder.a; // premultiply alpha fix for dark shadows.\n  }\n\n  float colTexAlphaMin = 0.5;\n  float frostAlphaMax = 0.6;\n  */\n\n  //Back face, cube interior \n  if( mode == 0 ) { \n\n/*\n    vec4 colTint = vec4( tintColor, tintAlpha );\n    // vec4 colBorderFaded = vec4( colBorder.rgb, colBorder.a * 0.6 );\n    \n    // col = alphaBlend( colBorderFaded, colTint );\n    col =  colTint ;\n\n    // below 0.0, means we're inside the cube.\n    if( cubeToCameraDistNorm < 0.0 ) { \n      \n      vec4 colTex = texture(texBack, uv); //back texture?\n      float colTexAlpha = map( cubeToCameraDistNorm, 0.0, -0.5, colTexAlphaMin, 1.0 );\n      colTex.a *= colTexAlpha;\n\n      // float frostAlpha = map( cubeToCameraDistNorm, 0., -1., frostAlphaMax, 0. );\n      // vec4 frostCol = vec4(1., 1., 1., frostAlpha );\n\n      col = alphaBlend( colTex, col );\n      // col = alphaBlend( frostCol, col );\n\n    }\n*/\n    \n\n    vec3 p = vPosWorld;       //pos on surface\n    vec3 n = -vNormalWorld;   //normal on surface: flipped for the back \n    //TODO check normal map along with this flipped normal - but its not critical\n\n    vec3 rayDir = normalize(vViewDir); //already normalized, rename to viewDir\n    vec3 nDetailed = getDetailNormal(n); //normalized\n\n    vec4 colSurface = shadeBack(p, nDetailed, rayDir);\n    col = colSurface; \n\n  } \n\n  // Front Face, cube exterior \n  else { \n\n    /*\n    //clip front face?\n    if( cubeToCameraDistNorm < 0.0 ) {\n      discard; // below 0.0, means we're inside the cube.\n    }\n\n    vec4 colTex = warp( uv );\n\n    float colTexAlpha = map( cubeToCameraDistNorm, 1., .5, 1., colTexAlphaMin );\n    colTex.a *= colTexAlpha;\n\n    float frostAlpha = map( cubeToCameraDistNorm, 1., 0., 0., frostAlphaMax );\n    vec4 frostCol = vec4(1., 1., 1., frostAlpha );\n\n    col = colTex;\n    \n    col = alphaBlend( frostCol, col );\n    col = alphaBlend( colBorder, col );\n    */\n\n    vec3 p = vPosWorld;       //pos on surface\n    vec3 n = vNormalWorld;    //normal on surface\n\n    \n    vec3 rayDir = normalize(vViewDir); //already normalized, todo renme to viewDir\n    vec3 nDetailed = getDetailNormal(n); //normalized\n\n\n    vec4 colSurface = shadeFront(p, nDetailed, rayDir);\n    col = colSurface;\n  }\n\n  // col.xyz = pow(col.xyz, vec3(.4545) ); //gamma coorection\n\n  fragColor = col;\n}\n";

class TexSwitch {
  constructor() {
    this.texLoader = null; // to be initialized
    this.rgbeLoader = null; // to be initialized
    this.pmremGenerator = null; // to be initialized


    // normal maps
    this.texNormal = [
      'n9.jpg',
      'n12.jpg',
      'n14.jpg',
      'n16.jpg',
      // 'n17.jpg',
    ];


    this.texEnv = [
      'studio016.hdr',
      'empty_warehouse_01_1k.hdr',
      // 'old_bus_depot_1k.hdr',
      'hamburg_hbf_1k.hdr',
    ];

    this.texRoughness = [
      'h17.jpg',
      'h9.jpg',
      'h12.jpg',
      'h14.jpg',
      'h16.jpg',
    ];


    this.texNormalId = 0;
    this.texEnvId = 0;
    // this.texRoughnessId = 0; //TODO depr
  }

  loadNormalMap(materials, _texNormalId) {
    this.texNormalId = _texNormalId;
    this.texLoader.load('/libs/ejx/assets/normal/' + this.texNormal[_texNormalId], function(tex) {
      tex.encoding = sRGBEncoding;
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;

      for (let i = 0; i < materials.length; i++) {
        materials[i].uniforms.texNormal.value = tex;
        materials[i].uniformsNeedUpdate = true;
      }
    });
  }

  // loads env map
  loadEnvMap(materials, _texEnvId) {
    // const _this = this;
    this.texEnvId = _texEnvId;
    this.rgbeLoader.load('/libs/ejx/assets/env/' + this.texEnv[_texEnvId], function(tex) {
      tex.minFilter = NearestFilter;
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;
      tex.encoding = LinearEncoding;// TODO sRGBEncoding;//rn we convert it in the shader

      for (let i = 0; i < materials.length; i++) {
        materials[i].uniforms.texHDRI.value = tex;

        // preset hdri exposures
        if (i == 0) materials[i].uniforms.texEnvExposure.value = 1.;
        if (i == 1) materials[i].uniforms.texEnvExposure.value = .2;
        if (i == 2) materials[i].uniforms.texEnvExposure.value = .02;


        materials[i].uniformsNeedUpdate = true;
      }


      // tex.mapping = EquirectangularReflectionMapping;
      // tex.mapping = EquirectangularRefractionMapping;
      // tex.encoding = sRGBEncoding;
      // mat.envMap =  _this.pmremGenerator.fromEquirectangular(tex).texture;
      // mat.needsUpdate = true;
    });
  }


  loadRoughnessMap(mat, _texRoughnessId) {
    // texture switch


    // itterate thru textures
    // this.texRoughnessId++;
    // if(texRoughnessId>texRoughness.length-1) texRoughnessId=0;
    this.texRoughnessId = _texRoughnessId;
    // console.log(_texRoughnessId);
    this.texLoader.load('/libs/ejx/assets/rough/' + this.texRoughness[_texRoughnessId], function(tex) {
      tex.encoding = sRGBEncoding;

      mat.roughnessMap = tex;
      mat.needsUpdate = true;
    });
  }
}

/* eslint-disable max-len */
// forked from EJCube2 by @vladstorm

function initCubeMaterial() {
  let material = new RawShaderMaterial({
    vertexShader: shaderVert,
    fragmentShader: shaderFrag,

    uniforms: {

      mode: { value: 0 }, // TODO rename to faceDir
      time: { value: 1. },
      resolution: { value: new Vector2(0, 0) },
      texBack: { value: null },

      tintColor: { value: new Color(0x000000) },
      tintAlpha: { value: 0.2 },

      texHDRI: { value: null },
      texEnvExposure: { value: .2 },

      texNormal: { value: null },

      cubeToCameraDistNorm: { value: 1.0 },

      // camFOV: {value: 50.}, //TODO set
      // cameraPos: {value: new THREE.Vector3(0)},
    },
    side: DoubleSide,
    transparent: true,
  });

  return material;
}


// DEPRECATED
/*
function initPBRMaterial() {


  let material = new THREE.MeshPhysicalMaterial( {

    metalness: .0, //params.metalness,

    roughness: .15, //params.roughness,

    transmission: 1,
    opacity: 1,
    thickness: 2,

    envMap: null,
    envMapIntensity: 1.,

    roughnessMap: null,

    normalScale: new THREE.Vector2(.05),
    clearcoatNormalMap: null,
    normalMap: null,

    clearcoat: .05,

    // side: DoubleSide,
    side: FrontSide,
    transparent: true,
    depthWrite: false,
  });

  material.onBeforeCompile = shader => {
    shader.fragmentShader = shader.fragmentShader.replace('/vec4 diffuseColor.*;/', `
      // Assign whatever you want!
      vec4 diffuseColor = vec4(1., 0., 0., 1.);
    `)
  };

  return material;


}
*/

// ---------------------------------------------------------------- EJCube3
class EJCube3 extends EJCubeBase {
  constructor(container, gui) {
    super(container, gui);
    // vs
    this.style = EJCubeStyleGlass3;


    // this.geometry = EJCube.initCubeGeometry();
    this.geometry = new RoundedBoxGeometry(1, 1, 1, 4, .02);
    this.materialBack = initCubeMaterial();
    this.materialFront = initCubeMaterial();

    this.cubeBack = new Mesh(this.geometry, this.materialBack);
    this.cubeBack.name = 'cubeBack';
    this.cubeBack.material.side = BackSide;
    this.cubeBack.material.uniforms.mode.value = 0; // back face
    this.cubeBack.material.uniformsNeedUpdate = true;

    this.cubeFront = new Mesh(this.geometry, this.materialFront);
    this.cubeFront.name = 'cubeFront';
    this.cubeFront.material.side = FrontSide;
    this.cubeFront.material.uniforms.mode.value = 1; // front face
    this.cubeFront.material.uniformsNeedUpdate = true;


    // PBR tests
    // this.cubeFront0 = new Mesh(this.geometry, this.materialFront0);
    // this.cubeFront0.name = 'cubeFront';
    // this.cubeFront0.material.side = FrontSide;
    // this.cubeFront0.scale.set(1.1,1.1,1.1);
    // this.cubeFront0.scale = new THREE.Vector3(1.1);
    // this.cubeFront0.material.uniforms.mode.value = 1;
    // this.cubeFront0.material.uniformsNeedUpdate = true;


    this.container.add(this.cubeBack);
    this.container.add(this.cubeFront);
    // this.container.add(this.cubeFront0);

    // tex switch init
    let texSwitch = new TexSwitch();
    texSwitch.texLoader = new TextureLoader();
    texSwitch.rgbeLoader = new RGBELoader();
    // texSwitch.pmremGenerator = pmremGenerator;

    texSwitch.loadNormalMap([this.materialFront, this.materialBack], 1);
    texSwitch.loadEnvMap([this.materialFront, this.materialBack], 2);
    // texSwitch.loadRoughnessMap(this.materialFront, 3);


    if (this.guiFolder) {
      this.guiFolder.add(texSwitch, 'texNormalId', 0., texSwitch.texNormal.length - 1)
        .step(1).name('Normal Map').onChange(x => {
          texSwitch.loadNormalMap([this.materialFront, this.materialBack], x);
        });

      this.guiFolder.add(texSwitch, 'texEnvId', 0., texSwitch.texEnv.length - 1)
        .step(1).name('Env Map').onChange(x => {
          texSwitch.loadEnvMap([this.materialFront, this.materialBack], x);
        });

      this.guiFolder.add(this.materialFront.uniforms.texEnvExposure, 'value', 0., 2.)
        .name('Env Exposure').onChange(x => {
          this.materialBack.uniforms.texEnvExposure.value = x;
          this.materialBack.uniformsNeedUpdate = true;

          this.materialFront.uniforms.texEnvExposure.value = x;
          this.materialFront.uniformsNeedUpdate = true;
        });


      this.guiFolder.add(this.materialFront.uniforms.tintAlpha, 'value', 0., 1.)
        .name('Tint Alpha').onChange(x => {
          // this.materialBack.uniforms.mode.value = x;
          // this.materialBack.uniformsNeedUpdate = true;

          // this.materialFront.uniforms.mode.value = x;
          this.materialFront.uniformsNeedUpdate = true;
        });

      this.guiFolder.addColor(this.materialFront.uniforms.tintColor, 'value')
        .name('Tint Color').onChange(x => {
          // this.materialBack.uniforms.tintColor.value = x;
          // this.materialBack.uniformsNeedUpdate = true;

          this.materialFront.uniforms.tintColor.value = x;
          this.materialFront.uniformsNeedUpdate = true;
        });

      this.guiFolder.add(this, 'cubeToCameraThreshold', 0., 1.)
        .name('Cube Threshold').onChange(value => {
          this.cubeToCameraThreshold = value;
        });


      /*

      //PBR
      this.guiFolder.add(this.materialFront, 'metalness', 0., 1.).name('metalness').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });
      this.guiFolder.add(this.materialFront, 'roughness', 0., 1.).name('roughness').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(this.materialFront, 'envMapIntensity', 0., 5.).name('envMapIntensity').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(this.materialFront, 'transmission', 0., 1.).name('transmission').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(this.materialFront, 'opacity', 0., 1.).name('opacity').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });


      this.guiFolder.add(this.materialFront, 'thickness', 0., 5.).name('thickness').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(this.materialFront.normalScale, 'x', 0., 1.).name('normalScale').onChange(x => {
        this.materialFront.normalScale.set(x, x);
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(this.materialFront, 'clearcoat', 0., 1.).name('clearcoat').onChange(x => {
        this.materialFront.uniformsNeedUpdate = true;
      });

      this.guiFolder.add(texSwitch, 'texRoughnessId', 0., texSwitch.texRoughness.length-1).step( 1 ).name('Roughness Map Id').onChange(x => {
        texSwitch.loadRoughnessMap(this.materialFront, x);
      });
      */
    }
  }

  update(camera) {
    super.update(camera);

    this.materialBack.uniforms.cubeToCameraDistNorm.value = this.cubeToCameraDistNorm;
    this.materialBack.uniformsNeedUpdate = true;

    // this.materialFront.uniforms.cameraPos.value = camera.position;
    this.materialFront.uniforms.cubeToCameraDistNorm.value = this.cubeToCameraDistNorm;
    this.materialFront.uniformsNeedUpdate = true;
  }

  updateProps(texture, resolution) {
    super.updateProps(texture, resolution);

    let materialBackUpdate = false;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.texBack.value !== texture;
    materialBackUpdate = materialBackUpdate || this.materialBack.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialBackUpdate ) {
      this.materialBack.uniforms.texBack.value = texture;
      this.materialBack.uniforms.resolution.value = resolution;
      this.materialBack.uniformsNeedUpdate = true;
    }

    let materialFrontUpdate = false;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.texBack.value !== texture;
    materialFrontUpdate = materialFrontUpdate || this.materialFront.uniforms.resolution.value.equals( resolution ) == false;
    if ( materialFrontUpdate ) {
      this.materialFront.uniforms.texBack.value = texture;
      this.materialFront.uniforms.resolution.value = resolution;
      this.materialFront.uniformsNeedUpdate = true;
    }
  }

  dispose() {
    super.dispose();
  }
}

/* eslint-disable max-len */

class EJCubeMask {
  constructor(container) {
    this.container = container;

    this.cubeBackGeometry = new BoxGeometry(1, 1, 1);
    this.cubeBackGeometry.name = 'EJCubeMask_BackGeometry';
    this.cubeBackMaterial = new MeshBasicMaterial({
      name: 'EJCubeMask_BackMaterial',
      color: 0xff0000,
      side: BackSide,
      depthWrite: false,
      depthTest: false,
    });
    this.cubeBack = new Mesh(this.cubeBackGeometry, this.cubeBackMaterial);
    this.cubeBack.name = 'EJCubeMask_BackMesh';
    this.cubeBack.frustumCulled = false;
    this.container.add(this.cubeBack);

    const faceColorMasks = EJCube.getFaceColorMasks();
    this.cubeFront = new Object3D();
    this.cubeFrontFaces = [];
    for (let i = 0; i < 6; i++) {
      const faceColorMask = faceColorMasks[i];
      const cubeFaceGeom = new PlaneGeometry(1, 1);
      cubeFaceGeom.name = `EJCubeMask_FrontGeometry_${i}`;
      const cubeFaceMat = new MeshBasicMaterial({
        color: new Color(faceColorMask.x, faceColorMask.y, faceColorMask.z),
        side: FrontSide,
        depthWrite: false,
        depthTest: false,
      });
      cubeFaceGeom.name = `EJCubeMask_FrontMaterial_${i}`;
      const cubeFace = new Mesh(cubeFaceGeom, cubeFaceMat);
      cubeFaceGeom.name = `EJCubeMask_FrontMesh_${i}`;
      cubeFace.frustumCulled = false;
      if (i === 0) {
        cubeFace.position.z = 0.5;
      } else if (i === 1) {
        cubeFace.position.x = 0.5;
        cubeFace.rotation.y = Math.PI * 0.5;
      } else if (i === 2) {
        cubeFace.position.z = -0.5;
        cubeFace.rotation.y = Math.PI;
      } else if (i === 3) {
        cubeFace.position.x = -0.5;
        cubeFace.rotation.y = -Math.PI * 0.5;
      } else if (i === 4) {
        cubeFace.position.y = 0.5;
        cubeFace.rotation.x = -Math.PI * 0.5;
      } else if (i === 5) {
        cubeFace.position.y = -0.5;
        cubeFace.rotation.x = Math.PI * 0.5;
      }
      this.cubeFrontFaces.push( cubeFace );
      this.cubeFront.add( cubeFace );
    }
    this.container.add(this.cubeFront);
  }

  setFaceColorMask(cubeFaceIndex, colorMask) {
    const cubeFace = this.cubeFrontFaces[cubeFaceIndex];
    cubeFace.material.color.r = colorMask.x;
    cubeFace.material.color.g = colorMask.y;
    cubeFace.material.color.b = colorMask.z;
  }

  update(elapsed, delta) {
    //
  }
  /**
   * Gets the screenspace box of the current context.
   *
   * @param {PerspectiveCamera} camera Camera to transform the bounding box with
   * @return {Box2} Bounding box of the mesh
   */
  getScreenspaceBox(camera) {
    const mesh = this.cubeBack;
    const vertices = mesh.geometry.attributes.position.array;
    const vertex = new THREE.Vector3();
    const min = new THREE.Vector3(1, 1, 1);
    const max = new THREE.Vector3(-1, -1, -1);

    const temp = new THREE.Vector3();
    for (let i = 0; i < vertices.length; i += 3) {
      temp.set(...vertices.slice(i, i + 3));
      const vertexWorldCoord = vertex.copy(temp).applyMatrix4(mesh.matrixWorld);
      const vertexScreenSpace = vertexWorldCoord.project(camera);
      min.min(vertexScreenSpace);
      max.max(vertexScreenSpace);
    }

    return new THREE.Box2(min, max);
  }
}

/* eslint-disable max-len */


class EJEnv {
  constructor(container, camera) {
    this.container = container;
    this.camera = camera;

    this.texture = new TextureLoader().load('./libs/ejx/assets/env/deathstar-bourgeois.jpg', this.textureLoaded.bind(this));
    this.texture.encoding = sRGBEncoding;

    this.geometry = new PlaneGeometry(1, 1, 1, 1);
    this.geometry.name = 'EJEnvGeometry';
    this.material = new MeshBasicMaterial({
      name: 'EJEnvMaterial',
      map: this.texture,
      color: new Color(0xffffff),
      opacity: 1.0,
      transparent: false,
      depthTest: false,
      depthWrite: false,
    });
    this.plane = new Mesh(this.geometry, this.material);
    this.plane.name = 'EJEnvMesh';
    this.plane.rotation.y = -Math.PI;
    this.plane.visible = false;

    this.container.add(this.plane);
  }

  textureLoaded(texture) {
    this.textureWidth = texture.image.width;
    this.textureHeight = texture.image.height;
    this.textureAspect = this.textureWidth / this.textureHeight;
    this.plane.visible = true;
  }

  fitPlaneToScreen(plane, camera, textureAspect) {
    let planeHeight = 1.0;
    let distance = planeHeight * 0.5 / Math.tan(camera.fov * 0.5 * (Math.PI / 180));
    let cameraDir = new Vector3();
    camera.getWorldDirection(cameraDir);
    plane.position.set(camera.position.x, camera.position.y, camera.position.z);
    plane.position.add(cameraDir.multiplyScalar(distance));
    plane.rotation.setFromRotationMatrix(camera.matrix);
    plane.scale.set(textureAspect, 1.0, 1.0);
  }

  update() {
    this.fitPlaneToScreen(this.plane, this.camera, this.textureAspect);
  }
}

const EJEnvTypeNone = 'None';
const EJEnvTypeImage = 'Image';

class EJEnvCamera {
  constructor(container, camera, cameraTexture) {
    this.container = container;
    this.camera = camera;
    this.texture = cameraTexture;

    // TODO: check that cameraTexture is always equal to window dimensions.
    this.textureWidth = window.innerWidth;
    this.textureHeight = window.innerHeight;
    this.textureAspect = this.textureWidth / this.textureHeight;

    this.geometry = new PlaneGeometry(1, 1, 1, 1);
    this.geometry.name = 'EJEnvCameraGeometry';
    this.material = new MeshBasicMaterial({
      name: 'EJEnvCameraMaterial',
      map: this.texture,
      color: new Color(0xffffff),
      opacity: 1.0,
      transparent: false,
      depthTest: false,
      depthWrite: false,
    });
    this.plane = new Mesh(this.geometry, this.material);
    this.plane.rotation.y = -Math.PI;

    this.container.add(this.plane);
  }

  fitPlaneToScreen(plane, camera, textureAspect) {
    let planeHeight = 1.0;
    let distance = planeHeight * 0.5 / Math.tan(camera.fov * 0.5 * (Math.PI / 180));
    let cameraDir = new Vector3();
    camera.getWorldDirection(cameraDir);
    plane.position.set(camera.position.x, camera.position.y, camera.position.z);
    plane.position.add(cameraDir.multiplyScalar(distance));
    plane.rotation.setFromRotationMatrix(camera.matrix);
    plane.scale.set(textureAspect, 1.0, 1.0);
  }

  update() {
    this.fitPlaneToScreen(this.plane, this.camera, this.textureAspect);
  }
}

/**
 * Full-screen textured quad shader
 */

const CopyShader = {

  uniforms: {

    'tDiffuse': { value: null },
    'opacity': { value: 1.0 },

  },

  vertexShader: /* glsl */`

		varying vec2 vUv;

		void main() {

			vUv = uv;
			gl_Position = vec4( position, 1.0 );

		}`,

  fragmentShader: /* glsl */`

		uniform float opacity;

		uniform sampler2D tDiffuse;

		varying vec2 vUv;

		void main() {

			gl_FragColor = texture2D( tDiffuse, vUv );
			gl_FragColor.a *= opacity;


		}`,

};

class Pass {
  constructor() {
    // if set to true, the pass is processed by the composer
    this.enabled = true;

    // if set to true, the pass indicates to swap read and write buffer after rendering
    this.needsSwap = true;

    // if set to true, the pass clears its buffer before rendering
    this.clear = false;

    // if set to true, the result of the pass is rendered to screen. This is set automatically by EffectComposer.
    this.renderToScreen = false;
  }

  setSize( /* width, height */ ) {}

  render( /* renderer, writeBuffer, readBuffer, deltaTime, maskActive */ ) {
    console.error( 'THREE.Pass: .render() must be implemented in derived pass.' );
  }

  dispose() {}
}

// Helper for passes that need to fill the viewport with a single quad.

const _camera = new OrthographicCamera( - 1, 1, 1, - 1, 0, 1 );

// https://github.com/mrdoob/three.js/pull/21358

const _geometry = new BufferGeometry();
_geometry.setAttribute( 'position', new Float32BufferAttribute( [- 1, 3, 0, - 1, - 1, 0, 3, - 1, 0], 3 ) );
_geometry.setAttribute( 'uv', new Float32BufferAttribute( [0, 2, 0, 0, 2, 0], 2 ) );

class FullScreenQuad {
  constructor( material ) {
    this._mesh = new Mesh( _geometry, material );
    this._mesh.frustumCulled = false;
  }

  dispose() {
    this._mesh.geometry.dispose();
  }

  render( renderer ) {
    renderer.render( this._mesh, _camera );
  }

  get material() {
    return this._mesh.material;
  }

  set material( value ) {
    this._mesh.material = value;
  }
}

class ShaderPass extends Pass {
  constructor( shader, textureID ) {
    super();

    this.textureID = ( textureID !== undefined ) ? textureID : 'tDiffuse';

    if ( shader instanceof ShaderMaterial ) {
      this.uniforms = shader.uniforms;

      this.material = shader;
    } else if ( shader ) {
      this.uniforms = UniformsUtils.clone( shader.uniforms );

      this.material = new ShaderMaterial( {

        defines: Object.assign( {}, shader.defines ),
        uniforms: this.uniforms,
        vertexShader: shader.vertexShader,
        fragmentShader: shader.fragmentShader,

      } );
    }

    this.fsQuad = new FullScreenQuad( this.material );
  }

  render( renderer, writeBuffer, readBuffer /* , deltaTime, maskActive */ ) {
    if ( this.uniforms[this.textureID] ) {
      this.uniforms[this.textureID].value = readBuffer.texture;
    }

    this.fsQuad.material = this.material;

    if ( this.renderToScreen ) {
      renderer.setRenderTarget( null );
      this.fsQuad.render( renderer );
    } else {
      renderer.setRenderTarget( writeBuffer );
      // TODO: Avoid using autoClear properties, see https://github.com/mrdoob/three.js/pull/15571#issuecomment-465669600
      if ( this.clear ) renderer.clear( renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil );
      this.fsQuad.render( renderer );
    }
  }

  dispose() {
    this.material.dispose();

    this.fsQuad.dispose();
  }
}

class MaskPass extends Pass {

	constructor( scene, camera ) {

		super();

		this.scene = scene;
		this.camera = camera;

		this.clear = true;
		this.needsSwap = false;

		this.inverse = false;

	}

	render( renderer, writeBuffer, readBuffer /*, deltaTime, maskActive */ ) {

		const context = renderer.getContext();
		const state = renderer.state;

		// don't update color or depth

		state.buffers.color.setMask( false );
		state.buffers.depth.setMask( false );

		// lock buffers

		state.buffers.color.setLocked( true );
		state.buffers.depth.setLocked( true );

		// set up stencil

		let writeValue, clearValue;

		if ( this.inverse ) {

			writeValue = 0;
			clearValue = 1;

		} else {

			writeValue = 1;
			clearValue = 0;

		}

		state.buffers.stencil.setTest( true );
		state.buffers.stencil.setOp( context.REPLACE, context.REPLACE, context.REPLACE );
		state.buffers.stencil.setFunc( context.ALWAYS, writeValue, 0xffffffff );
		state.buffers.stencil.setClear( clearValue );
		state.buffers.stencil.setLocked( true );

		// draw into the stencil buffer

		renderer.setRenderTarget( readBuffer );
		if ( this.clear ) renderer.clear();
		renderer.render( this.scene, this.camera );

		renderer.setRenderTarget( writeBuffer );
		if ( this.clear ) renderer.clear();
		renderer.render( this.scene, this.camera );

		// unlock color and depth buffer for subsequent rendering

		state.buffers.color.setLocked( false );
		state.buffers.depth.setLocked( false );

		// only render where stencil is set to 1

		state.buffers.stencil.setLocked( false );
		state.buffers.stencil.setFunc( context.EQUAL, 1, 0xffffffff ); // draw if == 1
		state.buffers.stencil.setOp( context.KEEP, context.KEEP, context.KEEP );
		state.buffers.stencil.setLocked( true );

	}

}

class ClearMaskPass extends Pass {

	constructor() {

		super();

		this.needsSwap = false;

	}

	render( renderer /*, writeBuffer, readBuffer, deltaTime, maskActive */ ) {

		renderer.state.buffers.stencil.setLocked( false );
		renderer.state.buffers.stencil.setTest( false );

	}

}

class EffectComposer {
  constructor( renderer, renderTarget ) {
    this.renderer = renderer;

    if ( renderTarget === undefined ) {
      const size = renderer.getSize( new Vector2() );
      this._pixelRatio = renderer.getPixelRatio();
      this._width = size.width;
      this._height = size.height;

      renderTarget = new WebGLRenderTarget( this._width * this._pixelRatio, this._height * this._pixelRatio );
      renderTarget.texture.name = 'EffectComposer.rt1';
    } else {
      this._pixelRatio = 1;
      this._width = renderTarget.width;
      this._height = renderTarget.height;
    }

    this.renderTarget1 = renderTarget;
    this.renderTarget2 = renderTarget.clone();
    this.renderTarget2.texture.name = 'EffectComposer.rt2';

    this.writeBuffer = this.renderTarget1;
    this.readBuffer = this.renderTarget2;

    this.renderToScreen = true;

    this.passes = [];

    this.copyPass = new ShaderPass( CopyShader );

    this.clock = new Clock();
  }

  swapBuffers() {
    const tmp = this.readBuffer;
    this.readBuffer = this.writeBuffer;
    this.writeBuffer = tmp;
  }

  addPass( pass ) {
    this.passes.push( pass );
    pass.setSize( this._width * this._pixelRatio, this._height * this._pixelRatio );
  }

  insertPass( pass, index ) {
    this.passes.splice( index, 0, pass );
    pass.setSize( this._width * this._pixelRatio, this._height * this._pixelRatio );
  }

  removePass( pass ) {
    const index = this.passes.indexOf( pass );

    if ( index !== - 1 ) {
      this.passes.splice( index, 1 );
    }
  }

  isLastEnabledPass( passIndex ) {
    for ( let i = passIndex + 1; i < this.passes.length; i ++ ) {
      if ( this.passes[i].enabled ) {
        return false;
      }
    }

    return true;
  }

  render( deltaTime ) {
    // deltaTime value is in seconds

    if ( deltaTime === undefined ) {
      deltaTime = this.clock.getDelta();
    }

    const currentRenderTarget = this.renderer.getRenderTarget();

    let maskActive = false;

    for ( let i = 0, il = this.passes.length; i < il; i ++ ) {
      const pass = this.passes[i];

      if ( pass.enabled === false ) continue;

      pass.renderToScreen = ( this.renderToScreen && this.isLastEnabledPass( i ) );
      pass.render( this.renderer, this.writeBuffer, this.readBuffer, deltaTime, maskActive );

      if ( pass.needsSwap ) {
        if ( maskActive ) {
          const context = this.renderer.getContext();
          const stencil = this.renderer.state.buffers.stencil;

          // context.stencilFunc( context.NOTEQUAL, 1, 0xffffffff );
          stencil.setFunc( context.NOTEQUAL, 1, 0xffffffff );

          this.copyPass.render( this.renderer, this.writeBuffer, this.readBuffer, deltaTime );

          // context.stencilFunc( context.EQUAL, 1, 0xffffffff );
          stencil.setFunc( context.EQUAL, 1, 0xffffffff );
        }

        this.swapBuffers();
      }

      if ( MaskPass !== undefined ) {
        if ( pass instanceof MaskPass ) {
          maskActive = true;
        } else if ( pass instanceof ClearMaskPass ) {
          maskActive = false;
        }
      }
    }

    this.renderer.setRenderTarget( currentRenderTarget );
  }

  reset( renderTarget ) {
    if ( renderTarget === undefined ) {
      const size = this.renderer.getSize( new Vector2() );
      this._pixelRatio = this.renderer.getPixelRatio();
      this._width = size.width;
      this._height = size.height;

      renderTarget = this.renderTarget1.clone();
      renderTarget.setSize( this._width * this._pixelRatio, this._height * this._pixelRatio );
    }

    this.renderTarget1.dispose();
    this.renderTarget2.dispose();
    this.renderTarget1 = renderTarget;
    this.renderTarget2 = renderTarget.clone();

    this.writeBuffer = this.renderTarget1;
    this.readBuffer = this.renderTarget2;
  }

  setSize( width, height ) {
    this._width = width;
    this._height = height;

    const effectiveWidth = this._width * this._pixelRatio;
    const effectiveHeight = this._height * this._pixelRatio;

    this.renderTarget1.setSize( effectiveWidth, effectiveHeight );
    this.renderTarget2.setSize( effectiveWidth, effectiveHeight );

    for ( let i = 0; i < this.passes.length; i ++ ) {
      this.passes[i].setSize( effectiveWidth, effectiveHeight );
    }
  }

  setPixelRatio( pixelRatio ) {
    this._pixelRatio = pixelRatio;

    this.setSize( this._width, this._height );
  }

  dispose() {
    this.renderTarget1.dispose();
    this.renderTarget2.dispose();

    this.copyPass.dispose();
  }
}

/**
 * Luminosity
 * http://en.wikipedia.org/wiki/Luminosity
 */

const LuminosityHighPassShader = {

  shaderID: 'luminosityHighPass',

  uniforms: {

    'tDiffuse': { value: null },
    'luminosityThreshold': { value: 1.0 },
    'smoothWidth': { value: 1.0 },
    'defaultColor': { value: new Color( 0x000000 ) },
    'defaultOpacity': { value: 0.0 },

  },

  vertexShader: /* glsl */`

		varying vec2 vUv;

		void main() {

			vUv = uv;

			gl_Position = vec4( position, 1.0 );

		}`,

  fragmentShader: /* glsl */`

		uniform sampler2D tDiffuse;
		uniform vec3 defaultColor;
		uniform float defaultOpacity;
		uniform float luminosityThreshold;
		uniform float smoothWidth;

		varying vec2 vUv;

		void main() {

			vec4 texel = texture2D( tDiffuse, vUv );

			vec3 luma = vec3( 0.299, 0.587, 0.114 );

			float v = dot( texel.xyz, luma );

			vec4 outputColor = vec4( defaultColor.rgb, defaultOpacity );

			float alpha = smoothstep( luminosityThreshold, luminosityThreshold + smoothWidth, v );

			gl_FragColor = mix( outputColor, texel, alpha );

		}`,

};

/**
 * UnrealBloomPass is inspired by the bloom pass of Unreal Engine. It creates a
 * mip map chain of bloom textures and blurs them with different radii. Because
 * of the weighted combination of mips, and because larger blurs are done on
 * higher mips, this effect provides good quality and performance.
 *
 * Reference:
 * - https://docs.unrealengine.com/latest/INT/Engine/Rendering/PostProcessEffects/Bloom/
 */
class UnrealBloomPass extends Pass {
  constructor( resolution, strength, radius, threshold ) {
    super();

    this.strength = ( strength !== undefined ) ? strength : 1;
    this.radius = radius;
    this.threshold = threshold;
    this.resolution = ( resolution !== undefined ) ? new Vector2( resolution.x, resolution.y ) : new Vector2( 256, 256 );

    // create color only once here, reuse it later inside the render function
    this.clearColor = new Color( 0, 0, 0 );

    // render targets
    this.renderTargetsHorizontal = [];
    this.renderTargetsVertical = [];
    this.nMips = 5;
    let resx = Math.round( this.resolution.x / 2 );
    let resy = Math.round( this.resolution.y / 2 );

    this.renderTargetBright = new WebGLRenderTarget( resx, resy );
    this.renderTargetBright.texture.name = 'UnrealBloomPass.bright';
    this.renderTargetBright.texture.generateMipmaps = false;

    for ( let i = 0; i < this.nMips; i ++ ) {
      const renderTargetHorizonal = new WebGLRenderTarget( resx, resy );

      renderTargetHorizonal.texture.name = 'UnrealBloomPass.h' + i;
      renderTargetHorizonal.texture.generateMipmaps = false;

      this.renderTargetsHorizontal.push( renderTargetHorizonal );

      const renderTargetVertical = new WebGLRenderTarget( resx, resy );

      renderTargetVertical.texture.name = 'UnrealBloomPass.v' + i;
      renderTargetVertical.texture.generateMipmaps = false;

      this.renderTargetsVertical.push( renderTargetVertical );

      resx = Math.round( resx / 2 );

      resy = Math.round( resy / 2 );
    }

    // luminosity high pass material

    if ( LuminosityHighPassShader === undefined ) {
      console.error( 'THREE.UnrealBloomPass relies on LuminosityHighPassShader' );
    }

    const highPassShader = LuminosityHighPassShader;
    this.highPassUniforms = UniformsUtils.clone( highPassShader.uniforms );

    this.highPassUniforms['luminosityThreshold'].value = threshold;
    this.highPassUniforms['smoothWidth'].value = 0.01;

    this.materialHighPassFilter = new ShaderMaterial( {
      uniforms: this.highPassUniforms,
      vertexShader: highPassShader.vertexShader,
      fragmentShader: highPassShader.fragmentShader,
      defines: {},
    } );

    // Gaussian Blur Materials
    this.separableBlurMaterials = [];
    const kernelSizeArray = [3, 5, 7, 9, 11];
    resx = Math.round( this.resolution.x / 2 );
    resy = Math.round( this.resolution.y / 2 );

    for ( let i = 0; i < this.nMips; i ++ ) {
      this.separableBlurMaterials.push( this.getSeperableBlurMaterial( kernelSizeArray[i] ) );

      this.separableBlurMaterials[i].uniforms['texSize'].value = new Vector2( resx, resy );

      resx = Math.round( resx / 2 );

      resy = Math.round( resy / 2 );
    }

    // Composite material
    this.compositeMaterial = this.getCompositeMaterial( this.nMips );
    this.compositeMaterial.uniforms['blurTexture1'].value = this.renderTargetsVertical[0].texture;
    this.compositeMaterial.uniforms['blurTexture2'].value = this.renderTargetsVertical[1].texture;
    this.compositeMaterial.uniforms['blurTexture3'].value = this.renderTargetsVertical[2].texture;
    this.compositeMaterial.uniforms['blurTexture4'].value = this.renderTargetsVertical[3].texture;
    this.compositeMaterial.uniforms['blurTexture5'].value = this.renderTargetsVertical[4].texture;
    this.compositeMaterial.uniforms['bloomStrength'].value = strength;
    this.compositeMaterial.uniforms['bloomRadius'].value = 0.1;
    this.compositeMaterial.needsUpdate = true;

    const bloomFactors = [1.0, 0.8, 0.6, 0.4, 0.2];
    this.compositeMaterial.uniforms['bloomFactors'].value = bloomFactors;
    this.bloomTintColors = [new Vector3( 1, 1, 1 ), new Vector3( 1, 1, 1 ), new Vector3( 1, 1, 1 ), new Vector3( 1, 1, 1 ), new Vector3( 1, 1, 1 )];
    this.compositeMaterial.uniforms['bloomTintColors'].value = this.bloomTintColors;

    // copy material
    if ( CopyShader === undefined ) {
      console.error( 'THREE.UnrealBloomPass relies on CopyShader' );
    }

    const copyShader = CopyShader;

    this.copyUniforms = UniformsUtils.clone( copyShader.uniforms );
    this.copyUniforms['opacity'].value = 1.0;

    this.materialCopy = new ShaderMaterial( {
      uniforms: this.copyUniforms,
      vertexShader: copyShader.vertexShader,
      fragmentShader: copyShader.fragmentShader,
      blending: AdditiveBlending,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    } );

    this.enabled = true;
    this.needsSwap = false;

    this._oldClearColor = new Color();
    this.oldClearAlpha = 1;

    this.basic = new MeshBasicMaterial( { transparent: true } );

    this.fsQuad = new FullScreenQuad( null );
  }

  dispose() {
    for ( let i = 0; i < this.renderTargetsHorizontal.length; i ++ ) {
      this.renderTargetsHorizontal[i].dispose();
    }

    for ( let i = 0; i < this.renderTargetsVertical.length; i ++ ) {
      this.renderTargetsVertical[i].dispose();
    }

    this.renderTargetBright.dispose();
  }

  setSize( width, height ) {
    let resx = Math.round( width / 2 );
    let resy = Math.round( height / 2 );

    this.renderTargetBright.setSize( resx, resy );

    for ( let i = 0; i < this.nMips; i ++ ) {
      this.renderTargetsHorizontal[i].setSize( resx, resy );
      this.renderTargetsVertical[i].setSize( resx, resy );

      this.separableBlurMaterials[i].uniforms['texSize'].value = new Vector2( resx, resy );

      resx = Math.round( resx / 2 );
      resy = Math.round( resy / 2 );
    }
  }

  render( renderer, writeBuffer, readBuffer, deltaTime, maskActive ) {
    if (window.spector) window.spector.setMarker('Effect: Bloom');
    renderer.getClearColor( this._oldClearColor );
    this.oldClearAlpha = renderer.getClearAlpha();
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    renderer.setClearColor( this.clearColor, 0 );

    if ( maskActive ) renderer.state.buffers.stencil.setTest( false );

    // Render input to screen

    if ( this.renderToScreen ) {
      this.fsQuad.material = this.basic;
      this.basic.map = readBuffer.texture;

      renderer.setRenderTarget( null );
      renderer.clear();
      this.fsQuad.render( renderer );
    }

    // 1. Extract Bright Areas

    this.highPassUniforms['tDiffuse'].value = readBuffer.texture;
    this.highPassUniforms['luminosityThreshold'].value = this.threshold;
    this.fsQuad.material = this.materialHighPassFilter;

    renderer.setRenderTarget( this.renderTargetBright );
    renderer.clear();
    this.fsQuad.render( renderer );

    // 2. Blur All the mips progressively

    let inputRenderTarget = this.renderTargetBright;

    for ( let i = 0; i < this.nMips; i ++ ) {
      this.fsQuad.material = this.separableBlurMaterials[i];

      this.separableBlurMaterials[i].uniforms['colorTexture'].value = inputRenderTarget.texture;
      this.separableBlurMaterials[i].uniforms['direction'].value = UnrealBloomPass.BlurDirectionX;
      renderer.setRenderTarget( this.renderTargetsHorizontal[i] );
      renderer.clear();
      this.fsQuad.render( renderer );

      this.separableBlurMaterials[i].uniforms['colorTexture'].value = this.renderTargetsHorizontal[i].texture;
      this.separableBlurMaterials[i].uniforms['direction'].value = UnrealBloomPass.BlurDirectionY;
      renderer.setRenderTarget( this.renderTargetsVertical[i] );
      renderer.clear();
      this.fsQuad.render( renderer );

      inputRenderTarget = this.renderTargetsVertical[i];
    }

    // Composite All the mips

    this.fsQuad.material = this.compositeMaterial;
    this.compositeMaterial.uniforms['bloomStrength'].value = this.strength;
    this.compositeMaterial.uniforms['bloomRadius'].value = this.radius;
    this.compositeMaterial.uniforms['bloomTintColors'].value = this.bloomTintColors;

    renderer.setRenderTarget( this.renderTargetsHorizontal[0] );
    renderer.clear();
    this.fsQuad.render( renderer );

    // Blend it additively over the input texture

    this.fsQuad.material = this.materialCopy;
    this.copyUniforms['tDiffuse'].value = this.renderTargetsHorizontal[0].texture;

    if ( maskActive ) renderer.state.buffers.stencil.setTest( true );

    if ( this.renderToScreen ) {
      renderer.setRenderTarget( null );
      this.fsQuad.render( renderer );
    } else {
      renderer.setRenderTarget( readBuffer );
      this.fsQuad.render( renderer );
    }

    // Restore renderer settings

    renderer.setClearColor( this._oldClearColor, this.oldClearAlpha );
    renderer.autoClear = oldAutoClear;
  }

  getSeperableBlurMaterial( kernelRadius ) {
    return new ShaderMaterial( {

      defines: {
        'KERNEL_RADIUS': kernelRadius,
        'SIGMA': kernelRadius,
      },

      uniforms: {
        'colorTexture': { value: null },
        'texSize': { value: new Vector2( 0.5, 0.5 ) },
        'direction': { value: new Vector2( 0.5, 0.5 ) },
      },

      vertexShader:
				`varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = vec4( position, 1.0 );
				}`,

      fragmentShader:
				`#include <common>
				varying vec2 vUv;
				uniform sampler2D colorTexture;
				uniform vec2 texSize;
				uniform vec2 direction;

				float gaussianPdf(in float x, in float sigma) {
					return 0.39894 * exp( -0.5 * x * x/( sigma * sigma))/sigma;
				}
				void main() {
					vec2 invSize = 1.0 / texSize;
					float fSigma = float(SIGMA);
					float weightSum = gaussianPdf(0.0, fSigma);
					vec4 diffuseSum = texture2D( colorTexture, vUv) * weightSum;
					for( int i = 1; i < KERNEL_RADIUS; i ++ ) {
						float x = float(i);
						float w = gaussianPdf(x, fSigma);
						vec2 uvOffset = direction * invSize * x;
						vec4 sample1 = texture2D( colorTexture, vUv + uvOffset);
						vec4 sample2 = texture2D( colorTexture, vUv - uvOffset);
						diffuseSum += (sample1 + sample2) * w;
						weightSum += 2.0 * w;
					}
					gl_FragColor = vec4(diffuseSum/weightSum);
				}`,
    } );
  }

  getCompositeMaterial( nMips ) {
    return new ShaderMaterial( {

      defines: {
        'NUM_MIPS': nMips,
      },

      uniforms: {
        'blurTexture1': { value: null },
        'blurTexture2': { value: null },
        'blurTexture3': { value: null },
        'blurTexture4': { value: null },
        'blurTexture5': { value: null },
        'dirtTexture': { value: null },
        'bloomStrength': { value: 1.0 },
        'bloomFactors': { value: null },
        'bloomTintColors': { value: null },
        'bloomRadius': { value: 0.0 },
      },

      vertexShader:
				`varying vec2 vUv;
				void main() {
					vUv = uv;
					gl_Position = vec4( position, 1.0 );
				}`,

      fragmentShader:
				`varying vec2 vUv;
				uniform sampler2D blurTexture1;
				uniform sampler2D blurTexture2;
				uniform sampler2D blurTexture3;
				uniform sampler2D blurTexture4;
				uniform sampler2D blurTexture5;
				uniform sampler2D dirtTexture;
				uniform float bloomStrength;
				uniform float bloomRadius;
				uniform float bloomFactors[NUM_MIPS];
				uniform vec3 bloomTintColors[NUM_MIPS];

				float lerpBloomFactor(const in float factor) {
					float mirrorFactor = 1.2 - factor;
					return mix(factor, mirrorFactor, bloomRadius);
				}

				void main() {
					gl_FragColor = bloomStrength * ( lerpBloomFactor(bloomFactors[0]) * vec4(bloomTintColors[0], 1.0) * texture2D(blurTexture1, vUv) +
						lerpBloomFactor(bloomFactors[1]) * vec4(bloomTintColors[1], 1.0) * texture2D(blurTexture2, vUv) +
						lerpBloomFactor(bloomFactors[2]) * vec4(bloomTintColors[2], 1.0) * texture2D(blurTexture3, vUv) +
						lerpBloomFactor(bloomFactors[3]) * vec4(bloomTintColors[3], 1.0) * texture2D(blurTexture4, vUv) +
						lerpBloomFactor(bloomFactors[4]) * vec4(bloomTintColors[4], 1.0) * texture2D(blurTexture5, vUv) );
				}`,
    } );
  }
}

UnrealBloomPass.BlurDirectionX = new Vector2( 1.0, 0.0 );
UnrealBloomPass.BlurDirectionY = new Vector2( 0.0, 1.0 );

function ejxPassToRealPass(pass, renderTarget) {
  if (pass.type === "bloom") {
    const strength = pass.strength ?? pass.bloomStrength;
    const radius = pass.radius ?? pass.bloomRadius;
    const threshold = pass.threshold ?? pass.bloomThreshold;
    return new UnrealBloomPass(
      new Vector2(renderTarget.width, renderTarget.height),
      strength,
      radius,
      threshold
    );
  }
}
class EJPostProcessing {
  constructor(renderer, renderTarget, gui = null) {
    this.renderer = renderer;
    this.renderTarget = renderTarget;
    this.passLookup = /* @__PURE__ */ new Map();
    this.composer = new EffectComposer(renderer, renderTarget);
    this.composer.renderToScreen = false;
  }
  render() {
    this.renderer.xr.enabled = false;
    if (window.spector)
      window.spector.setMarker("Post processing");
    if (this.composer.readBuffer !== this.renderTarget) {
      this.composer.swapBuffers();
    }
    this.composer.render();
    if (window.spector)
      window.spector.setMarker("END: Post processing");
    this.renderer.xr.enabled = true;
  }
  /**
   * Adds a post processing pass
   * @param pass - 
   * @returns 
   */
  addPass(pass) {
    const realPass = ejxPassToRealPass(pass, this.renderTarget);
    this.composer.addPass(realPass);
    this.passLookup.set(pass.type, realPass);
    return realPass;
  }
  /**
   * Adds a post processing pass
   * @param pass - 
   * @returns 
   */
  removePass(type) {
    const realPass = this.passLookup.get(type);
    if (!realPass) {
      console.warn(`EJPostProcessing: Attempted to remove "${type}" pass but none exists.`);
      return;
    }
    this.passLookup.delete(type);
    this.composer.removePass(realPass);
    return realPass;
  }
  /**
   * Adds a post processing pass
   * @param pass - 
   * @returns 
   */
  hasPass(type) {
    return this.passLookup.get(type);
  }
  /**
   * Configures a post processing pass
   * @param type - Pass type you're trying to configure
   * @param config - The config for the pass.
   */
  configurePass(type, config) {
    const realPass = this.passLookup.get(type);
    if (!realPass) {
      console.warn(`EJPostProcessing: Attempted to configure "${type}" pass but none exists.`);
      return;
    }
    switch (type) {
      case "bloom":
        {
          const pass = realPass;
          pass.strength = config.strength;
          pass.radius = config.radius;
          pass.threshold = config.threshold;
        }
        break;
    }
  }
  /**
   * @returns The final render target of the EffectComposer
   */
  getLastRenderTarget() {
    return this.composer.readBuffer;
  }
  /**
   * Resizes the effect composer
   */
  resize(width, height) {
    this.composer.setSize(width, height);
  }
  /** Disposes of the post processing effects. */
  dispose() {
    this.composer.dispose();
  }
}

/* eslint-disable max-len */

// ---------------------------------------------------------------- Color Management
ColorManagement.enabled = true;

// ---------------------------------------------------------------- EJWebGLRenderer
let EJWebGLRenderer$1 = class EJWebGLRenderer extends WebGLRenderer {
  constructor(options) {
    super(options);

    this.renderFunc = this.render;
    this.onBeforeRenderScene = null;
    this.onAfterRenderScene = null;

    this.render = (scene, camera) => {
      const onBeforeRenderSaved = scene.onBeforeRender;
      const onAfterRenderSaved = scene.onAfterRender;

      if (this.onBeforeRenderScene) {
        scene.onBeforeRender = this.onBeforeRenderScene;
      }
      if (this.onAfterRenderScene) {
        scene.onAfterRender = this.onAfterRenderScene;
      }
      //
      this.renderFunc(scene, camera);
      //
      scene.onBeforeRender = onBeforeRenderSaved;
      scene.onAfterRender = onAfterRenderSaved;
    };
  }
};

// ---------------------------------------------------------------- EJPlayer
/** @typedef EJPlayerConfig
 * @property {HTMLCanvasElement} canvas
 * @property {THREE.WebGLRenderer} renderer
 * @property {THREE.PerspectiveCamera} camera
 * @property {THREE.Scene} scene
 * @property {THREE.Texture} cameraTexture
 */

/**
 * @class EJPlayer
 * @classdesc Entrypoint for playing EJX Content'
 * @property {EJPlayerConfig} config
 */
class EJPlayer {
  /**
   * @param {EJPlayerConfig} config Config for EJPlayer
   */
  constructor(config) {
    if (EJPlayer.Instance) {
      return EJPlayer.Instance;
    }
    EJPlayer.Instance = this;

    this.config = config;
    if (this.config == null) {
      this.config = {
        gui: null,
      };
    }

    const w = window.innerWidth;
    const h = window.innerHeight;
    const r = window.devicePixelRatio;
    this.res = new Vector2(w, h);
    this.resPixels = new Vector2(w * r, h * r);
    this.devicePixelRatio = r;

    this.play = 1;
    this.time = 0.0;
    this.clock = new Clock(false);
    this.contents = []; // possible future support for multiple content running at the same time.

    this.contentConfigOriginal = [];
    this.contentConfigCurrent = [];
    this.contentConfigInside = null;

    this.camerasData = [];
    this.cameraData = {
      camera: null,
      inside: false,
      insideChanged: false,
      insideFaceIndex: -1,
      px: false, // positive x flag
      nx: false, // negative x flag
      py: false, // positive y flag
      ny: false, // negative y flag
      pz: false, // positive z flag
      nz: false, // negative z flag
    };
    this.cameraData0 = null;

    this.renderOverrideFunc = null;
    this.resizeOverrideFunc = null;

    this.clippingPlanesEmpty = Object.freeze( [] );
    this.clippingPlanesCube = [];
    const faceNormals = EJCube.getFaceNormals();
    for (let i = 0; i < faceNormals.length; i++) {
      const faceNormal = faceNormals[i];
      const plane = new Plane( faceNormal, 0.51 );
      this.clippingPlanesCube.push( plane );
    }

    this.saveImageFlag = false;

    this.init(this.config);

    this.render();
  }

  /**
   * @param {EJPlayerConfig} config
   */
  init(config) {
    const { canvas, renderer, camera, scene, cameraTexture } = config;

    if ( canvas ) {
      this.canvas = canvas;
    } else {
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'ej-renderer';
    }

    let context = this.canvas.getContext('webgl2');

    if ( renderer ) {
      this.renderer = renderer;
    } else {
      this.renderer = new EJWebGLRenderer$1({
        canvas: this.canvas,
        context: context,
        alpha: true,
        antialias: true,
      });
      this.renderer.setSize(this.res.x, this.res.y);
      this.renderer.setPixelRatio(this.devicePixelRatio);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.autoClear = false;
      this.renderer.clippingPlanes = this.clippingPlanesEmpty;
      this.renderer.xr.enabled = true;
      // color grading.
      this.renderer.outputEncoding = sRGBEncoding;
      this.renderer.toneMapping = NoToneMapping;
      this.renderer.toneMappingExposure = 1;
      this.renderer.useLegacyLights = false;
    }

    if (!canvas) document.body.appendChild(this.renderer.domElement);

    if ( camera ) {
      this.camera = camera;
    } else {
      this.camera = new PerspectiveCamera(20, this.res.x / this.res.y, 0.01, 1000);
      this.camera.position.set(0, 0, 5);
      this.camera.lookAt(0, 0, 0);
      this.camera.name = 'xrCamera';
    }

    this.cameraTestMesh = null;

    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControls.enableDamping = true;
    this.orbitControls.minDistance = 0;
    this.orbitControls.maxDistance = 10;

    if ( scene ) {
      this.scene = scene;
    } else {
      this.scene = new Scene();
      this.scene.background = null;
    }

    if ( cameraTexture ) {
      this.cameraTexture = cameraTexture; // camera pixels as texture (optional)
    } else {
      this.cameraTexture = null;
    }

    this.initCommon();

    this.cubeInit(EJCubeStylePlain);

    this.inputEventsInit();

    window.addEventListener('resize', this.resizeHandler.bind(this));

    this.renderer.setAnimationLoop( this.animationLoop.bind(this) );
  }

  initCommon() {
    this.renderTargetContent = new WebGLRenderTarget(this.resPixels.x, this.resPixels.y, {
      wrapS: ClampToEdgeWrapping,
      wrapT: ClampToEdgeWrapping,
      magFilter: LinearFilter,
      minFilter: LinearFilter,
      generateMipmaps: false,
      format: RGBAFormat,
      type: UnsignedByteType,
      anisotropy: Texture.anisotropy,
      encoding: sRGBEncoding,
      depthBuffer: true,
      stencilBuffer: true,
      samples: 0,
    });
    this.renderTargetContentMasked = this.renderTargetContent.clone();
    this.renderTargetMask = this.renderTargetContent.clone();

    this.renderTargetContent.texture.name = 'EJ.renderTargetContent';
    this.renderTargetContentMasked.texture.name = 'EJ.renderTargetContentMasked';
    this.renderTargetMask.texture.name = 'EJ.renderTargetMask';

    this.renderTargets = [
      this.renderTargetContent,
      this.renderTargetContentMasked,
      this.renderTargetMask,
    ];

    this.cubeMasterOpacity = 1.0;
    this.cubeMasterOpacitySaved = 1.0;

    this.cubeContainer = new Object3D();
    this.scene.add( this.cubeContainer );

    this.cubeMask = new EJCubeMask(new Object3D(), null);
    this.cubeMaskScene = new Scene();
    this.cubeMaskScene.add( this.cubeMask.container );
    this.cubeMaskPass = this.initMaskPass();
    this.cubeMaskPassCamera = new OrthographicCamera( - 1, 1, 1, - 1, 0, 1 );

    this.loadingManager = new LoadingManager();

    this.postProcessing = null;
  }

  initMaskPass() {
    const geom = new PlaneGeometry( 2, 2 );
    const mat = new ShaderMaterial({
      uniforms: {
        texContent: { type: 't', value: this.renderTargetContent.texture },
        texMask: { type: 't', value: this.renderTargetMask.texture },
        color: { value: new Vector3(0, 0, 0) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main(){ 
          vUv = uv;
          gl_Position = vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D texContent;
        uniform sampler2D texMask;
        uniform vec3 color;
        varying vec2 vUv;
        
        void main(){
          vec4 colorMask = texture2D( texMask, vUv);
          if( all( equal( colorMask.rgb, color ) ) ) {
            gl_FragColor = texture2D( texContent, vUv);
            return;
          }
          discard;
        }
      `,
    });
    const mesh = new Mesh(geom, mat);
    mesh.name = 'EJPlayer_MaskPassMesh';
    mesh.frustumCulled = false;
    return mesh;
  }

  animationLoop( timestamp, frame ) {
    if ( this.renderOverrideFunc !== null ) {
      this.renderOverrideFunc( timestamp, frame );
      return;
    }
    this.render( timestamp, frame );
  }

  render( timestamp, frame ) {
    if (this.clock.running == false) {
      this.clock.start();
    }
    let timeDelta = this.clock.getDelta();
    timeDelta *= this.play;
    this.time += timeDelta;

    if (this.orbitControls) {
      this.orbitControls.update();
    }

    this.cubeContainer.updateMatrixWorld( true );

    const cameraDataUpdate = cameraData => {
      const cubeMatrix = new Matrix4();
      cubeMatrix.copy(this.cubeContainer.matrixWorld).invert();
      const pos = new Vector3(cameraData.camera.position.x, cameraData.camera.position.y, cameraData.camera.position.z);
      pos.applyMatrix4( cubeMatrix );
      const cubeSize = 0.5;
      const px = pos.x <= cubeSize;
      const nx = pos.x >= -cubeSize;
      const py = pos.y <= cubeSize;
      const ny = pos.y >= -cubeSize;
      const pz = pos.z <= cubeSize;
      const nz = pos.z >= -cubeSize;
      const inside = px && nx && py && ny && pz && nz;
      cameraData.insideChanged = cameraData.inside != inside;
      cameraData.inside = inside;
      if (cameraData.insideChanged) {
        if (cameraData.inside) {
          if ( cameraData.px != px ) { // positive x entry (right face)
            cameraData.insideFaceIndex = 1;
          } else if ( cameraData.nx != nx ) { // negative x entry (left face)
            cameraData.insideFaceIndex = 3;
          } else if ( cameraData.py != py ) { // positive y entry (top face)
            cameraData.insideFaceIndex = 4;
          } else if ( cameraData.ny != ny ) { // negative y entry (bottom face)
            cameraData.insideFaceIndex = 5;
          } else if ( cameraData.pz != pz ) { // positive z entry (front face)
            cameraData.insideFaceIndex = 0;
          } else if ( cameraData.nz != nz ) { // negative z entry (back face)
            cameraData.insideFaceIndex = 2;
          }
        }
      }
      cameraData.px = px;
      cameraData.nx = nx;
      cameraData.py = py;
      cameraData.ny = ny;
      cameraData.pz = pz;
      cameraData.nz = nz;
    };

    let cameraIndex = 0;
    const cameraTestOnBeforeRender = ( renderer, scene, camera ) => {
      if ( this.camerasData.length === cameraIndex ) {
        this.camerasData.push( Object.assign({}, this.cameraData) );
      }
      const cameraData = this.camerasData[cameraIndex];
      cameraData.camera = camera;
      cameraDataUpdate( cameraData );
      cameraIndex += 1;
    };
    if ( !this.cameraTestMesh ) {
      this.cameraTestMesh = new Mesh(); // used for running a quick test render and saving the camera value.
      this.cameraTestMesh.frustumCulled = false; // always render, even if not in camera view.
    }
    this.cameraTestMesh.onBeforeRender = cameraTestOnBeforeRender;
    this.renderer.render( this.cameraTestMesh, this.camera );
    if ( this.camerasData.length > cameraIndex && cameraIndex >= 1 ) { // do not remove all cameras, leave at least one.
      this.camerasData.splice(cameraIndex, this.camerasData.length - cameraIndex );
    }
    this.cameraData0 = this.camerasData[0];

    this.envUpdate();

    this.cubeUpdate(this.cameraData0.camera);

    const content = this.contentCurrent();
    if (content ) {
      this.renderContent( content );
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  renderContent(content) {
    if (!content.instance) {
      return;
    }
    if (!content.instance.render) {
      console.log('content.instance.render does not exist.');
      return;
    }

    let postProcessingInit = true;
    postProcessingInit = postProcessingInit && !this.postProcessing;
    postProcessingInit = postProcessingInit && content.instance.postProcessing;
    if ( postProcessingInit ) {
      const postProcessingConfig = content.instance.postProcessing();
      if ( postProcessingConfig ) {
        this.postProcessing = new EJPostProcessing(
          this.renderer,
          this.renderTargetContentMasked,
          null
        );
        this.postProcessing.addPass(postProcessingConfig);
      }
    }

    if (!this.cube) {
      this.renderer.render(this.scene, this.camera);
      if (content.instance.update) content.instance.update();
      content.instance.render();
      return;
    }

    const renderTargetSaved = this.renderer.getRenderTarget();
    if (renderTargetSaved && this.renderer.xr.isPresenting) {
      this.resize(renderTargetSaved.width, renderTargetSaved.height, window.devicePixelRatio, true);
    }

    content.camera.copy(this.camera);

    content.scene.position.copy( this.cubeContainer.position );
    content.scene.quaternion.copy( this.cubeContainer.quaternion );
    content.scene.scale.copy( this.cubeContainer.scale );

    // clipping planes.

    let useClippingPlanesOld = (this.renderer.clippingPlanes.length > 0);
    let useClippingPlanesNew = false;
    if ( content.instance.useClippingPlanes ) {
      useClippingPlanesNew = content.instance.useClippingPlanes();
    }
    let useClippingPlanesChanged = useClippingPlanesNew !== useClippingPlanesOld;
    if (useClippingPlanesChanged) {
      if (useClippingPlanesNew) {
        this.renderer.clippingPlanes = this.clippingPlanesCube;
      } else {
        this.renderer.clippingPlanes = this.clippingPlanesEmpty;
      }
    }

    const faceNormals = EJCube.getFaceNormals();
    const faceCentres = EJCube.getFaceCentres();

    const facesVisible = [];
    const cubeNormalMatrix = new Matrix3().getNormalMatrix( this.cubeContainer.matrixWorld );
    for (let i = 0; i < faceNormals.length; i++) {
      faceNormals[i].applyMatrix3( cubeNormalMatrix ).normalize();
    }
    for (let i = 0; i < faceCentres.length; i++) {
      faceCentres[i].applyMatrix4( this.cubeContainer.matrixWorld );
    }
    this.camerasData.forEach( cameraData => {
      const camera = cameraData.camera;
      for (let i = 0; i < 6; i++) {
        const dirToCamera = camera.position.clone().sub( faceCentres[i] );
        const facingCamera = dirToCamera.dot( faceNormals[i] ) > 0;
        if ( facingCamera === false ) {
          continue;
        }
        const faceOnScreen = EJCube.getFaceOnScreen( i, this.cubeContainer.matrixWorld, camera );
        if ( faceOnScreen === false ) {
          continue;
        }
        const faceIndexFound = facesVisible.findIndex( faceIndex => faceIndex === i );
        if ( faceIndexFound === -1 ) {
          // since the check is based on up to 2 cameras (VR),
          // faceIndex might have already been added from the previous camera,
          // we don't want duplicated.
          facesVisible.push( i );
        }
      }
    });

    if ( this.cameraData0.insideChanged ) {
      if (this.cameraData0.inside) {
        this.contentConfigCurrent.forEach(config => {
          const cubeFaceIndex = config.cubeFaceIndices.find( cubeFaceIndex => cubeFaceIndex === this.cameraData0.insideFaceIndex );
          if (cubeFaceIndex !== undefined) {
            this.contentConfigInside = config;
            this.contentConfigInside.isInsidePortal = true;
          }
        });
      } else {
        this.contentConfigInside.isInsidePortal = false;
        this.contentConfigInside = null;
      }
    }

    const contentColorMasks = EJCube.getFaceColorMasks();
    this.contentConfigOriginal = [];
    if ( content.instance ) {
      if ( content.instance.contentPerCubeFace ) {
        const contentPerCubeFaceNew = content.instance.contentPerCubeFace();
        if ( contentPerCubeFaceNew ) {
          const contentIDs = [];
          contentIDs.push({
            contentID: contentPerCubeFaceNew.front ? contentPerCubeFaceNew.front : 0,
            cubeFaceIndex: 0,
          });
          contentIDs.push({
            contentID: contentPerCubeFaceNew.right ? contentPerCubeFaceNew.right : 0,
            cubeFaceIndex: 1,
          });
          contentIDs.push({
            contentID: contentPerCubeFaceNew.back ? contentPerCubeFaceNew.back : 0,
            cubeFaceIndex: 2,
          });
          contentIDs.push({
            contentID: contentPerCubeFaceNew.left ? contentPerCubeFaceNew.left : 0,
            cubeFaceIndex: 3,
          });
          contentIDs.push({
            contentID: contentPerCubeFaceNew.top ? contentPerCubeFaceNew.top : 0,
            cubeFaceIndex: 4,
          });
          contentIDs.push({
            contentID: contentPerCubeFaceNew.bottom ? contentPerCubeFaceNew.bottom : 0,
            cubeFaceIndex: 5,
          });
          while (contentIDs.length > 0) {
            const contentIDFirst = contentIDs.splice(0, 1)[0];
            const cubeFaceIndices = [contentIDFirst.cubeFaceIndex];
            for (let i = 0; i < contentIDs.length; i++) {
              const contentIDObj = contentIDs[i];
              if (contentIDObj.contentID === contentIDFirst.contentID) {
                const contentIDNext = contentIDs.splice(i--, 1)[0];
                cubeFaceIndices.push(contentIDNext.cubeFaceIndex);
              }
            }
            this.contentConfigOriginal.push({
              contentID: contentIDFirst.contentID,
              cubeFaceIndices: cubeFaceIndices,
              colorMask: contentColorMasks.splice(0, 1)[0],
            });
          }
        }
      }
    }
    if (this.contentConfigOriginal.length === 0) {
      this.contentConfigOriginal = [
        {
          contentID: 0,
          cubeFaceIndices: [0, 1, 2, 3, 4, 5],
          colorMask: contentColorMasks[0],
        },
      ];
    }

    this.contentConfigCurrent = [];
    this.contentConfigOriginal.forEach(config => {
      this.contentConfigCurrent.push({
        contentID: config.contentID,
        cubeFaceIndices: [...config.cubeFaceIndices],
        colorMask: new Vector3().copy(config.colorMask),
        isInsidePortal: false,
      });
    });

    this.contentConfigCurrent.forEach(config => {
      config.cubeFaceIndices.forEach(cubeFaceIndex => {
        this.cubeMask.setFaceColorMask(cubeFaceIndex, config.colorMask);
      });
    });

    if (window.spector) window.spector.setMarker('Render: Cube Mask Scene');
    this.renderer.setRenderTarget(this.renderTargetMask);
    this.renderer.clear();

    this.cubeMaskScene.position.copy( this.cubeContainer.position );
    this.cubeMaskScene.quaternion.copy( this.cubeContainer.quaternion );
    this.cubeMaskScene.scale.copy( this.cubeContainer.scale );

    this.renderer.render(this.cubeMaskScene, this.camera);

    let contentToRender = [];
    if (this.contentConfigInside) {
      contentToRender = [this.contentConfigInside];
    } else {
      this.contentConfigCurrent.forEach(config => {
        config.cubeFaceIndices.forEach(cubeFaceIndex => {
          const index = facesVisible.findIndex( faceIndex => faceIndex === cubeFaceIndex );
          if ( index >= 0 ) {
            contentToRender.push(config);
          }
        });
      });
    }

    if (content.instance.update) {
      content.instance.update();
    }

    let contentRendering = null;

    const onBeforeRenderContent = ( renderer, scene, camera ) => {
      this.renderer.setScissorTest(false); // reset scissoring from possible prevous VR camera pass.

      let doScissor = true; // set to false to disable scissor.
      doScissor = doScissor && contentRendering.isInsidePortal === false;
      if ( doScissor === false ) {
        return;
      }
      // position and scale have been reset at this point, so cubes are rendered at 1 unit.
      const position = new Vector3(0, 0, 0);
      const scale = new Vector3(1, 1, 1);
      const matrixWorld = new Matrix4();
      matrixWorld.compose( position, this.cubeContainer.quaternion, scale );
      //
      const viewportBox = new Box2();
      viewportBox.min.x = 0;
      viewportBox.min.y = 0;
      viewportBox.max.x = this.resPixels.x;
      viewportBox.max.y = this.resPixels.y;
      if ( camera.viewport ) {
        viewportBox.max.x = camera.viewport.z;
        viewportBox.max.y = camera.viewport.w;
      }
      const scissorBox = new Box2();
      for (let i = 0; i < contentRendering.cubeFaceIndices.length; i++) {
        const faceIndex = contentRendering.cubeFaceIndices[i];
        if ( facesVisible.find( index => index === faceIndex ) === undefined ) {
          continue; // this face is not visible.
        }
        const faceBoundingBox = EJCube.getFaceBoundingBox( faceIndex, matrixWorld, camera );
        if ( faceBoundingBox === null ) {
          continue;
        }
        faceBoundingBox.min.x *= viewportBox.max.x;
        faceBoundingBox.max.x *= viewportBox.max.x;
        faceBoundingBox.min.y *= viewportBox.max.y;
        faceBoundingBox.max.y *= viewportBox.max.y;
        if ( scissorBox.isEmpty() ) {
          scissorBox.copy( faceBoundingBox );
        } else {
          scissorBox.union( faceBoundingBox );
        }
      }
      if ( scissorBox.isEmpty() ) {
        return;
      }
      if ( scissorBox.equals( viewportBox ) ) {
        return;
      }
      const scissorX = scissorBox.min.x + (camera.viewport ? camera.viewport.x : 0);
      const scissorY = viewportBox.max.y - scissorBox.max.y;
      const scissorW = scissorBox.max.x - scissorBox.min.x;
      const scissorH = scissorBox.max.y - scissorBox.min.y;
      let scissorError = false;
      scissorError = scissorError || ( scissorW <= 0 );
      scissorError = scissorError || ( scissorH <= 0 );
      if ( scissorError ) {
        return;
      }
      this.renderer.setScissorTest(true);
      this.renderer.setScissor(
        scissorX / this.devicePixelRatio,
        scissorY / this.devicePixelRatio,
        scissorW / this.devicePixelRatio,
        scissorH / this.devicePixelRatio
      );
    };
    const cameraProbe = new Mesh(); // used for running a quick test render and saving the camera value.
    cameraProbe.frustumCulled = false; // always render, even if not in camera view.
    cameraProbe.name = 'camera-probe';
    cameraProbe.renderOrder = -Infinity; // make it render first. one issue, backgrounds will always render first.
    cameraProbe.onBeforeRender = onBeforeRenderContent;

    const cameraMatrixSaved = [
      new Matrix4(),
      new Matrix4(),
    ];
    const sceneScaleSaved = new Vector3();
    const scenePositionSaved = new Vector3();

    const onBeforeRenderScene = ( renderer, scene, camera ) => {
      // add camera probe as the very first child.
      // when its rendered, onBeforeRender will be called with the correct camera data.
      scene.add( cameraProbe );

      sceneScaleSaved.copy( scene.scale );
      scenePositionSaved.copy( scene.position );
      scene.scale.set(1, 1, 1); // make 1 unit again.
      scene.position.set(0, 0, 0); // make at centre again.
      scene.updateMatrixWorld( true );

      const cameraOnBeforeRender = camera => {
        const cameraPos = new Vector3(0, 0, 0);
        cameraPos.sub(camera.position);
        cameraPos.add(scenePositionSaved);
        cameraPos.multiplyScalar(-1.0 / sceneScaleSaved.x);

        camera.position.copy( cameraPos );
        camera.matrix.compose( camera.position, camera.quaternion, camera.scale );
        camera.matrixWorld.copy( camera.matrix );
        camera.updateMatrixWorld( true );
      };
      cameraMatrixSaved[0].copy( camera.matrix );
      cameraOnBeforeRender( camera );
      if ( camera.cameras ) {
        camera.cameras.forEach( (camera, i) => {
          cameraMatrixSaved[i].copy( camera.matrix );
          cameraOnBeforeRender( camera );
        });
      }
    };

    const onAfterRenderScene = ( renderer, scene, camera ) => {
      // camera probe is removed from the content scene.
      cameraProbe.removeFromParent();

      const cameraOnAfterRender = (camera, cameraMatrix) => {
        camera.matrix.copy( cameraMatrix );
        camera.matrix.decompose( camera.position, camera.quaternion, camera.scale );
        camera.updateMatrixWorld( true );
      };
      cameraOnAfterRender( camera, cameraMatrixSaved[0] ); // left eye.
      if ( camera.cameras ) {
        camera.cameras.forEach( (camera, i) => {
          cameraOnAfterRender( camera, cameraMatrixSaved[i] );
        });
      }
      scene.scale.copy( sceneScaleSaved );
      scene.position.copy( scenePositionSaved );
      scene.updateMatrixWorld( true );
    };

    for (let i = 0; i < contentToRender.length; i++) {
      const config = contentToRender[i];
      contentRendering = config;

      if (window.spector) window.spector.setMarker(`Rendering ${config.contentID}`);

      this.renderer.setRenderTarget(this.renderTargetContent);
      this.renderer.clear();

      this.renderer.onBeforeRenderScene = onBeforeRenderScene;
      this.renderer.onAfterRenderScene = onAfterRenderScene;

      content.instance.render( config.contentID );

      this.renderer.onBeforeRenderScene = null;
      this.renderer.onAfterRenderScene = null;

      if ( this.camerasData.length === 2 ) {
        // VR. need to disable scissor here.
        this.renderer.setScissorTest(false);
      }

      // when going through the front of the cube color mask, the near culling clips it and does not render the mask color.
      // to minimise this effect, the back of the cube color mask is also assigned the same color,
      // which minimised the flickering of content when transition between faces.
      if ( this.cameraData0.inside === false || config.isInsidePortal ) {
        this.cubeMask.cubeBackMaterial.color.setRGB( config.colorMask.x, config.colorMask.y, config.colorMask.z );
      }

      this.cubeMaskPass.material.uniforms.color.value = config.colorMask;
      this.cubeMaskPass.material.uniformsNeedUpdate = true;

      if (window.spector) window.spector.setMarker('Render: Cube Mask Pass');
      this.renderer.setRenderTarget(this.renderTargetContentMasked);
      if (i == 0) {
        this.renderer.clear();
      }
      const currentValue = this.renderer.xr.enabled;
      this.renderer.xr.enabled = false;
      this.renderer.render(this.cubeMaskPass, this.cubeMaskPassCamera);
      this.renderer.xr.enabled = currentValue;

      if ( this.camerasData.length === 1 ) {
        // single screen. desktop / mobile / AR - disable scissor here.
        this.renderer.setScissorTest(false);
      }
    }

    let texture = this.renderTargetContentMasked.texture;
    if ( this.postProcessing ) {
      this.postProcessing.render();
      texture = this.postProcessing.getLastRenderTarget().texture;
    }
    this.cubeUpdateProps( texture, this.resPixels );

    // this.renderer.outputEncoding = sRGBEncoding;
    // this.renderer.toneMapping = NoToneMapping;
    // this.renderer.toneMappingExposure = 1;
    // this.renderer.useLegacyLights = false;

    if ( this.saveImageFlag ) {
      this.saveImageFlag = false;
      this.renderExterior();
    }

    this.renderer.setRenderTarget(renderTargetSaved);

    if (window.spector) window.spector.setMarker('Rendering EJPlayer scene');
    this.renderer.render(this.scene, this.camera);
  }

  renderExterior() {
    if (window.spector) window.spector.setMarker('Render: Exteriqor');
    const content = this.contentCurrent();
    const contentCanRender = content && content.instance && content.instance.render;
    if (!contentCanRender) {
      return;
    }

    const res = this.res.clone();
    const retina = this.devicePixelRatio;
    const size = 512;
    this.resize( size, size, 1 );

    const renderTargetFace = this.renderTargetContent.clone();
    renderTargetFace.texture.name = 'EJ.renderTargetFace';
    renderTargetFace.texture.encoding = LinearEncoding;

    this.cubeContainer.matrix.identity(); // reset content scene to zero centre.
    this.cubeContainer.matrix.decompose(
      this.cubeContainer.position,
      this.cubeContainer.quaternion,
      this.cubeContainer.scale
    );
    this.cubeContainer.matrixWorld.decompose(
      this.cubeMaskScene.position,
      this.cubeMaskScene.quaternion,
      this.cubeMaskScene.scale
    );
    this.cubeContainer.matrix.decompose(
      content.scene.position,
      content.scene.quaternion,
      content.scene.scale
    );

    const cameraToRestore = this.camera.clone();
    const camera = this.camera.clone();

    let contentPerCubeFace = {};
    if ( content.instance.contentPerCubeFace ) {
      contentPerCubeFace = content.instance.contentPerCubeFace();
    }

    const faceNames = EJCube.getFaceDescriptors();
    const faceNormals = EJCube.getFaceNormals();
    const faceColorMasks = EJCube.getFaceColorMasks();
    const faceFilenames = ['pz', 'px', 'nz', 'nx', 'py', 'ny'];

    for (let i = 0; i < faceNames.length; i++) {
      const cameraDistance = 0.5 / Math.tan(camera.fov * 0.5 * (Math.PI / 180)) + 0.5;
      const cameraPosition = faceNormals[i];
      cameraPosition.multiplyScalar( cameraDistance );
      camera.position.copy( cameraPosition );
      camera.lookAt(0, 0, 0);

      if (window.spector) window.spector.setMarker(`Cube Content ${i}`);
      this.camera.copy(camera);
      content.camera.copy(camera);

      const colorMask = faceColorMasks[i];
      this.cubeMask.setFaceColorMask(i, colorMask);
      this.cubeMask.cubeBackMaterial.color.setRGB( colorMask.x, colorMask.y, colorMask.z );
      this.cubeMaskPass.material.uniforms.color.value = colorMask;
      this.cubeMaskPass.material.uniformsNeedUpdate = true;

      this.renderer.setRenderTarget(this.renderTargetMask);
      this.renderer.clear();
      this.renderer.render(this.cubeMaskScene, camera);

      this.renderer.setRenderTarget(this.renderTargetContent);
      this.renderer.clear();

      const faceName = faceNames[i];
      let contentID = 0;
      if ( contentPerCubeFace[faceName] ) {
        contentID = contentPerCubeFace[faceName];
      }
      content.instance.render( contentID );

      this.renderer.setRenderTarget(this.renderTargetContentMasked);
      this.renderer.clear();
      this.renderer.render(this.cubeMaskPass, this.cubeMaskPassCamera);

      this.renderer.setRenderTarget(renderTargetFace);
      this.renderer.clear();
      this.renderer.render(this.cubeContainer, camera);

      const pixels = new Uint8Array( size * size * 4 );
      const pixelsFlipped = new Uint8Array( size * size * 4 );

      this.renderer.readRenderTargetPixels(renderTargetFace, 0, 0, size, size, pixels);

      for ( let y = 0; y < size; y ++ ) {
        const srcOffset = y * size * 4;
        const dstOffset = ( size - y - 1 ) * size * 4;
        pixelsFlipped.set( pixels.subarray( srcOffset, srcOffset + size * 4 ), dstOffset );
      }

      const filename = faceFilenames[i] + '.png';
      this.saveImage( pixelsFlipped, size, size, filename );
    }

    renderTargetFace.dispose();

    content.camera.copy(cameraToRestore); // restore camera
    this.resize( res.x, res.y, retina ); // restore size.
  }

  dispose() {
    this.inputEventsKill();
  }

  // -------------------------------------------------------------- save image.
  requestSaveImage() {
    this.saveImageFlag = true;
  }

  saveImage(pixels, w, h, filename) {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    const imageData = ctx.createImageData(w, h);
    imageData.data.set(pixels);

    ctx.putImageData(imageData, 0, 0);
    canvas.toBlob(blob => {
      this.downloadImage(blob, filename);
    }, 'image/png');
  }

  downloadImage(blob, filename) {
    let link = document.createElement('a');
    document.body.appendChild(link); // Firefox requires the link to be in the body
    link.download = filename;
    link.href = URL.createObjectURL(blob);
    link.click();
    document.body.removeChild(link); // remove the link when done
  }

  // -------------------------------------------------------------- Resize
  resize( w = window.innerWidth, h = window.innerHeight, r = window.devicePixelRatio, forVR = false ) {
    if (this.res.x === w && this.res.y === h && this.devicePixelRatio === r) {
      return; // this resolution has already been set.
    }

    this.res = new Vector2(w, h);
    this.devicePixelRatio = r;

    if (forVR) {
      this.resPixels = new Vector2(w, h);
      // camera and renderer already updated by three.js
    } else {
      this.resPixels = new Vector2(w * r, h * r);
      this.camera.aspect = this.res.x / this.res.y;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.res.x, this.res.y);
      this.renderer.setPixelRatio(r);
    }

    this.renderTargets.forEach(renderTarget => {
      renderTarget.setSize(this.resPixels.x, this.resPixels.y);
    });

    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.resize) {
          content.instance.resize(this.resPixels.x, this.resPixels.y);
        }
      }
    }

    this.cubeUpdateProps(this.renderTargetContentMasked.texture, this.resPixels);

    if ( this.postProcessing ) {
      this.postProcessing.resize(this.resPixels.x, this.resPixels.y);
    }
  }

  resizeHandler() {
    if (this.resizeOverrideFunc !== null) {
      this.resizeOverrideFunc();
      return;
    }
    this.resize();
  }

  // -------------------------------------------------------------- Input Events
  inputEventsInit() {
    document.addEventListener( 'keydown', this.inputHandler.bind(this) );
    document.addEventListener( 'keyup', this.inputHandler.bind(this) );
    document.addEventListener( 'pointerdown', this.inputHandler.bind(this) );
    document.addEventListener( 'pointerup', this.inputHandler.bind(this) );
    document.addEventListener( 'pointermove', this.inputHandler.bind(this) );
  }

  inputEventsKill() {
    document.removeEventListener( 'keydown', this.inputHandler.bind(this) );
    document.removeEventListener( 'keyup', this.inputHandler.bind(this) );
    document.removeEventListener( 'pointerdown', this.inputHandler.bind(this) );
    document.removeEventListener( 'pointerup', this.inputHandler.bind(this) );
    document.removeEventListener( 'pointermove', this.inputHandler.bind(this) );
  }

  inputHandler( event ) {
    const content = this.contentCurrent();
    if (content && content.instance && content.instance[event.type]) {
      content.instance[event.type](event);
    }
  }

  // -------------------------------------------------------------- Environment
  envKill() {
    if (this.env == null) {
      return;
    }
    if (this.env.container.parent) {
      this.env.container.parent.remove(this.env.container);
    }
    this.env = null;
  }

  envCameraInit() {
    this.env = new EJEnvCamera(new Object3D(), this.camera, this.cameraTexture);
    this.scene.add(this.env.container);
  }

  envInit(envType) {
    this.envKill();

    if (envType == EJEnvTypeNone) {
      return;
    } else if (envType == EJEnvTypeImage) {
      this.env = new EJEnv(new Object3D(), this.camera);
    }
    this.scene.add(this.env.container);
  }

  envUpdate() {
    if (this.env) {
      this.env.update();
    }
  }

  // -------------------------------------------------------------- Cube
  cubeKill() {
    if (this.cube == null) {
      return;
    }
    this.cube.dispose();
    this.cube = null;
  }

  cubeInit(cubeStyle) {
    let validStyle = false;
    validStyle = validStyle || (cubeStyle === EJCubeStyleNone);
    validStyle = validStyle || (cubeStyle === EJCubeStylePlain);
    validStyle = validStyle || (cubeStyle === EJCubeStyleGlass);
    validStyle = validStyle || (cubeStyle === EJCubeStyleGlass3);
    if ( validStyle == false ) {
      return;
    }
    let sameStyle = true;
    sameStyle = sameStyle && (this.cube != null);
    sameStyle = sameStyle && (this.cube.style === cubeStyle);
    if ( sameStyle ) {
      return;
    }

    this.cubeKill();

    if (cubeStyle == EJCubeStyleNone) {
      return;
    } else if (cubeStyle == EJCubeStylePlain) {
      this.cube = new EJCube1(this.cubeContainer, this.config.gui);
    } else if (cubeStyle == EJCubeStyleGlass) {
      this.cube = new EJCube2(this.cubeContainer, this.config.gui);
    } else if (cubeStyle == EJCubeStyleGlass3) {
      this.cube = new EJCube3(this.cubeContainer, this.config.gui);
    }

    this.cubeUpdateProps(this.renderTargetContentMasked.texture, this.resPixels);
    this.cube.updateMasterOpacity( this.cubeMasterOpacity );
  }

  cubeUpdate(camera) {
    if (this.cube == null) {
      return;
    }
    this.cube.update(camera);

    const cubeMasterOpacityChanged = this.cubeMasterOpacitySaved != this.cubeMasterOpacity;
    this.cubeMasterOpacitySaved = this.cubeMasterOpacity;
    if ( cubeMasterOpacityChanged ) {
      this.cube.updateMasterOpacity( this.cubeMasterOpacity );
    }
  }

  cubeUpdateProps( texture, resolution ) {
    if (this.cube == null) {
      return;
    }
    this.cube.updateProps(texture, resolution);
  }

  // -------------------------------------------------------------- Content
  contentLoaded() {
    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.loaded) {
          const loaded = content.instance.loaded();
          return loaded;
        }
      }
      return true; // if content exists, assume it is loaded.
    }
    return false;
  }

  contentLoadProgress() {
    let progress = 0;
    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.loadProgress) {
          progress = content.instance.loadProgress();
        }
      }
    }
    return progress;
  }

  contentBox( boxOut ) {
    if (!this.boxDefault) {
      const boxCenter = new THREE.Vector3(0, 0, 0);
      const boxSize = new THREE.Vector3(1, 1, 1);
      this.boxDefault = new Box3();
      this.boxDefault.setFromCenterAndSize( boxCenter, boxSize );
    }
    boxOut.copy( this.boxDefault ); // cubes are always 1 unit box with center at zero.
  }

  contentCurrent() {
    if (this.contents.length == 0) {
      return null;
    }
    const content = this.contents[this.contents.length - 1];
    return content;
  }

  contentDispose(content) {
    if (content.instance) {
      if (content.instance.dispose) {
        content.instance.dispose();
      }
    }
    if (content.scene) {
      EJUtils.disposeRecursive(content.scene);
    }
    if (content.gui) {
      content.gui.destroy();
    }
    if (this.postProcessing) {
      this.postProcessing.dispose();
      this.postProcessing = null;
    }
  }

  contentInstantiate(module) {
    const content = this.contentCurrent();
    if (content == null) {
      return;
    }

    content.scene = new Scene();
    content.scene.name = 'Content-Scene';
    content.camera = this.camera;
    if (this.config.gui) {
      content.gui = this.config.gui.addFolder('Content Settings');
    }

    const urlModifier = url => {
      const path = content.filename;
      let pathModified = path.substring(0, path.lastIndexOf('/')) + '/' + url;
      return pathModified;
    };
    this.loadingManager.setURLModifier( urlModifier );

    content.instance = module.main({
      canvas: this.canvas,
      renderer: this.renderer,
      renderTarget: this.renderTargetContent,
      scene: content.scene,
      camera: content.camera,
      loadingManager: this.loadingManager,
      gui: content.gui,
    });

    if ( content.instance && content.instance.cubeStyle ) {
      const cubeStyle = content.instance.cubeStyle();
      this.cubeInit( cubeStyle );
    }
  }

  contentKill(content) {
    this.contentDispose(content);
  }

  contentKillAll() {
    this.contents.forEach(content => {
      this.contentKill(content);
    });
    this.contents.splice(0, this.contents.length); // clear contents array.
  }

  contentValidate(_filename) {
    // TODO: do some checks on the file being loaded, like is it a JS file etc.
    return true;
  }

  // INFO: LK I've modified the implementation to support passing in the module directly.
  // I don't think the library should assume how the content will be loaded and this
  // will make everything more versatile.

  // -------------------------------------------------------------- API
  setContent({ filename, module }) {
    if (this.contentValidate(filename) == false) {
      return; // content file is not valid.
    }

    this.contentKillAll();

    const content = {
      filename: filename,
      identifier: filename.split('.js')[0] + '-script',
      module,
      instance: null,
      scene: null,
      gui: null,
    };
    this.contents.push(content);

    this.contentInstantiate(module);
  }

  setCube(value) {
    this.cubeInit(value);
  }

  setEnvironment(value) {
    this.envInit(value);
  }

  /**
   * Gets the post processing manager for
   * @returns {import('./EJPostProcessing').EJPostProcessing} post processing manager
   */
  getPostProcessing() {
    return this.postProcessing;
  }

  setCubeConfig(value) {
    // const sampleValue = {
    //   borderOn: true,
    //   borderWidth: 0.0025,
    //   borderFeather: 0.5,
    //   borderColor: new Color( 1.0, 1.0, 1.0 ),
    //   borderAlpha: 0.25,
    // };
    if ( this.cube ) {
      this.cube.setConfig( value );
    }
  }
}

const movementSpeed = 0.004; // 0.03 for non XR content, 0.004 for XR
const rotationSpeed = 0.0015;

class NoClipControls {
  constructor(camera, canvas) {
    this.camera = camera;
    this.canvas = canvas;
    this.enabled = true;
    this.locked = false;

    this.camera.rotation.order = 'YXZ';
    this.direction = new Vector3();

    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;
    this.moveUp = false;
    this.moveDown = false;

    canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    canvas.addEventListener('click', this.requestPointerLock.bind(this));
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    document.addEventListener('keyup', this.handleKeyUp.bind(this));
    document.addEventListener('pointerlockchange', this.handlePointerLockChange.bind(this));
    document.addEventListener('mozpointerlockchange', this.handlePointerLockChange.bind(this));
    document.addEventListener('webkitpointerlockchange', this.handlePointerLockChange.bind(this));
  }

  setPosition(position) {
    this.camera.position.copy(position);
    this.camera.updateWorldMatrix();
  }

  setRotation(rotation) {
    this.camera.rotation.copy(rotation);
    this.camera.updateWorldMatrix();
  }

  update() {
    if (!this.enabled || !this.locked) return;
    this.camera.getWorldDirection(this.direction);
    if (this.moveForward) {
      this.camera.translateZ(-movementSpeed);
    } else if (this.moveBackward) {
      this.camera.translateZ(movementSpeed);
    }
    if (this.moveLeft) {
      this.camera.translateX(-movementSpeed);
    } else if (this.moveRight) {
      this.camera.translateX(movementSpeed);
    }
    if (this.moveUp) {
      this.camera.translateY(movementSpeed);
    } else if (this.moveDown) {
      this.camera.translateY(-movementSpeed);
    }
  }

  handleMouseMove(event) {
    if (!this.enabled || !this.locked) return;
    this.camera.rotation.y -= event.movementX * rotationSpeed;
    this.camera.rotation.x -= event.movementY * rotationSpeed;
  }

  handleKeyDown(event) {
    if (event.key === 'w') {
      this.moveForward = true;
      this.moveBackward = false;
    }
    if (event.key === 's') {
      this.moveForward = false;
      this.moveBackward = true;
    }
    if (event.key === 'a') {
      this.moveLeft = true;
      this.moveRight = false;
    }
    if (event.key === 'd') {
      this.moveLeft = false;
      this.moveRight = true;
    }
    if (event.key === ' ') {
      this.moveUp = true;
      this.moveDown = false;
    }
    if (event.key === 'c') {
      this.moveUp = false;
      this.moveDown = true;
    }
  }

  handleKeyUp(event) {
    if (event.key === 'w' && this.moveForward) this.moveForward = false;
    if (event.key === 's' && this.moveBackward) this.moveBackward = false;
    if (event.key === 'a' && this.moveLeft) this.moveLeft = false;
    if (event.key === 'd' && this.moveRight) this.moveRight = false;
    if (event.key === ' ' && this.moveUp) this.moveUp = false;
    if (event.key === 'c' && this.moveDown) this.moveDown = false;
  }

  requestPointerLock() {
    if (!this.enabled) return;
    if (this.canvas.requestPointerLock) this.canvas.requestPointerLock();
    if (this.canvas.mozRequestPointerLock) this.canvas.mozRequestPointerLock();
    if (this.canvas.webkitRequestPointerLock) this.canvas.webkitRequestPointerLock();
  }

  handlePointerLockChange() {
    this.locked = document.pointerLockElement === this.canvas ||
            document.mozPointerLockElement === this.canvas ||
            document.webkitPointerLockElement === this.canvas;
  }
}

class NoControls {
  constructor() {
    this.enabled = true;
  }

  update() {
  }
}

/* eslint-disable max-len */

// ---------------------------------------------------------------- Color Management
ColorManagement.enabled = true;

// ---------------------------------------------------------------- EJWebGLRenderer
class EJWebGLRenderer extends WebGLRenderer {
  constructor(options) {
    super(options);

    this.renderFunc = this.render;
    this.onBeforeRenderScene = null;
    this.onAfterRenderScene = null;

    this.render = (scene, camera) => {
      const onBeforeRenderSaved = scene.onBeforeRender;
      const onAfterRenderSaved = scene.onAfterRender;

      if (this.onBeforeRenderScene) {
        scene.onBeforeRender = this.onBeforeRenderScene;
      }
      if (this.onAfterRenderScene) {
        scene.onAfterRender = this.onAfterRenderScene;
      }
      //
      this.renderFunc(scene, camera);
      //
      scene.onBeforeRender = onBeforeRenderSaved;
      scene.onAfterRender = onAfterRenderSaved;
    };
  }
}

// ---------------------------------------------------------------- EJPlayerBase
/** @typedef EJPlayerConfig
 * @property {HTMLCanvasElement} canvas
 * @property {THREE.WebGLRenderer} renderer
 * @property {THREE.PerspectiveCamera} camera
 * @property {THREE.Scene} scene
 * @property {THREE.Texture} cameraTexture
 */

/**
 * @class EJPlayerBase
 * @classdesc Entrypoint for playing EJX Content'
 * @property {EJPlayerConfig} config
 */
class EJPlayerBase {
  /**
     * @param {EJPlayerConfig} config Config for EJPlayerBase
     */
  constructor(config) {
    this.config = config;
    if (this.config == null) {
      this.config = {
        gui: null,
      };
    }

    const w = window.innerWidth;
    const h = window.innerHeight;
    const r = window.devicePixelRatio;
    this.res = new Vector2(w, h);
    this.resPixels = new Vector2(w * r, h * r);
    this.devicePixelRatio = r;

    this.contents = []; // possible future support for multiple content running at the same time.

    this.renderOverrideFunc = null;
    this.resizeOverrideFunc = null;

    this.init(this.config);

    this.render();
  }

  /**
     * @param {EJPlayerConfig} config
     */
  init(config) {
    const { canvas, renderer, camera, scene, cameraTexture } = config;

    if ( canvas ) {
      this.canvas = canvas;
    } else {
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'ej-renderer';
    }

    let context = this.canvas.getContext('webgl2', {
      stencil: true, // It needs to be set in the context and not in the renderer options
    });

    if ( renderer ) {
      this.renderer = renderer;
    } else {
      this.renderer = new EJWebGLRenderer({
        canvas: this.canvas,
        context: context,
        alpha: true,
        antialias: true,
      });
      this.renderer.setSize(this.res.x, this.res.y);
      this.renderer.setPixelRatio(this.devicePixelRatio);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.autoClear = false;
      this.renderer.xr.enabled = true;
      // color grading.
      this.renderer.outputEncoding = sRGBEncoding;
      this.renderer.toneMapping = NoToneMapping;
      this.renderer.toneMappingExposure = 1;
      this.renderer.useLegacyLights = false;
      this.gl = context;
    }

    if (!canvas) document.body.appendChild(this.renderer.domElement);

    if ( camera ) {
      this.camera = camera;
    } else {
      // We need smallest near value possible to avoid near clipping using computePortalProjectionMatrix
      this.camera = new PerspectiveCamera(35, this.res.x / this.res.y, 0.01, 1000);
      this.camera.position.set(0, 0, 5);
      this.camera.lookAt(0, 0, 0);
      this.camera.name = 'xrCamera';
    }

    this.initControls();

    if ( scene ) {
      this.scene = scene;
    } else {
      this.scene = new Scene();
      this.scene.background = null;
    }

    if ( cameraTexture ) {
      this.cameraTexture = cameraTexture; // camera pixels as texture (optional)
    } else {
      this.cameraTexture = null;
    }

    this.commonInit();
    this.sceneInit();
    this.inputEventsInit();

    window.addEventListener('resize', this.resizeHandler.bind(this));

    this.renderer.setAnimationLoop(this.animationLoop.bind(this));
  }

  initControls() {
    this.camera.position.set(0, 0, 2);

    this.avilableControls = {};
    this.avilableControls.NoControls = new NoControls();
    this.avilableControls.NoClipControls = new NoClipControls(this.camera, this.renderer.domElement);
    this.avilableControls.NoClipControls.enabled = false;
    this.avilableControls.OrbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.avilableControls.OrbitControls.enabled = false;
    this.avilableControls.OrbitControls.enableDamping = true;
    this.avilableControls.OrbitControls.minDistance = 0;
    this.avilableControls.OrbitControls.maxDistance = 10;
    const guiValues = {
      controls: 'OrbitControls',
    };
    this.controls = this.avilableControls[guiValues.controls];
    this.controls.enabled = true;

    if (this.config.gui) {
      const gui = this.config.gui.addFolder('Controls');
      gui.add(guiValues, 'controls', ['OrbitControls', 'NoClipControls', 'NoControls'], guiValues.controls)
        .onChange(value => {
          Object.values(this.avilableControls).forEach(controls => controls.enabled = false);
          this.controls = this.avilableControls[value];
          this.controls.enabled = true;
          this.camera.position.set(0, 0, 2);
        });
    }
  }

  commonInit() {
    this.renderTargetContent = new WebGLRenderTarget(this.resPixels.x, this.resPixels.y, {
      wrapS: ClampToEdgeWrapping,
      wrapT: ClampToEdgeWrapping,
      magFilter: LinearFilter,
      minFilter: LinearFilter,
      generateMipmaps: false,
      format: RGBAFormat,
      type: UnsignedByteType,
      anisotropy: Texture.anisotropy,
      encoding: sRGBEncoding,
      depthBuffer: true,
      stencilBuffer: true,
      samples: 0,
    });
    this.renderTargetContentMasked = this.renderTargetContent.clone();
    this.renderTargetMask = this.renderTargetContent.clone();

    this.renderTargetContent.texture.name = 'EJ.renderTargetContent';
    this.renderTargetContentMasked.texture.name = 'EJ.renderTargetContentMasked';
    this.renderTargetMask.texture.name = 'EJ.renderTargetMask';

    this.renderTargets = [
      this.renderTargetContent,
      this.renderTargetContentMasked,
      this.renderTargetMask,
    ];

    this.masterOpacity = 1.0;

    this.contentContainer = new Object3D();

    this.loadingManager = new LoadingManager();
  }

  // -------------------------------------------------------------- Scene
  sceneInit() {
    this.contentFsQuadUvMap = new Vector4(0, 0, 1, 1);
    this.contentFsQuadMat = new RawShaderMaterial({
      uniforms: {
        texContent: { type: 't', value: this.renderTargetContent.texture },
        uvMap: { value: this.contentFsQuadUvMap },
        masterOpacity: { value: this.masterOpacity },
      },
      vertexShader: `
        precision highp float;
        precision highp int;

        attribute vec3 position;
        attribute vec2 uv;
        
        varying vec2 vUv;
        
        void main() {
          vUv = uv;
          gl_Position = vec4(position.x, position.y, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        precision highp int;

        uniform sampler2D texContent;
        uniform vec4 uvMap;
        uniform float masterOpacity;

        varying vec2 vUv;

        float lerp(float a, float b, float t) {
          return (b - a) * t + a;
        }
  
        vec4 LinearTosRGB( vec4 value ) {
          return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
        }      
  
        void main() {
          vec2 uv = vec2(
            lerp( uvMap.x, uvMap.z, vUv.x ),
            lerp( uvMap.y, uvMap.w, vUv.y )
          );
          vec4 color = texture2D( texContent, uv );
          color = LinearTosRGB( color );
          color.a *= masterOpacity;
          gl_FragColor = color;
        }
      `,
      side: DoubleSide,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    //
    this.contentFsQuad = new Mesh(
      new PlaneGeometry(2, 2),
      this.contentFsQuadMat
    );
    this.contentFsQuad.frustumCulled = false;
    this.contentFsQuad.onBeforeRender = ( renderer, scene, camera ) => {
      const cameras = this.renderer.xr.getCamera().cameras;
      const isVR = ( cameras.length === 2 ) && this.renderer.xr.isPresenting;
      if ( isVR ) {
        const leftEye = (cameras[0] === camera);
        if ( leftEye ) {
          this.contentFsQuadUvMap.set(0, 0, 0.5, 1.0).divideScalar( window.devicePixelRatio );
          this.contentFsQuadMat.uniforms.uvMap.value.copy(this.contentFsQuadUvMap);
          this.contentFsQuadMat.uniformsNeedUpdate = true;
        } else { // right eye.
          this.contentFsQuadUvMap.set(0.5, 0, 1.0, 1.0).divideScalar( window.devicePixelRatio );
          this.contentFsQuadMat.uniforms.uvMap.value.copy(this.contentFsQuadUvMap);
          this.contentFsQuadMat.uniformsNeedUpdate = true;
        }
      } else { // single camera - dektop and AR.
        this.contentFsQuadUvMap.set(0, 0, 1, 1);
        if ( !this.contentFsQuadMat.uniforms.uvMap.value.equals(this.contentFsQuadUvMap) ) {
          this.contentFsQuadMat.uniforms.uvMap.value.copy(this.contentFsQuadUvMap);
          this.contentFsQuadMat.uniformsNeedUpdate = true;
        }
      }
      if ( this.contentFsQuadMat.uniforms.masterOpacity.value != this.masterOpacity ) {
        this.contentFsQuadMat.uniforms.masterOpacity.value = this.masterOpacity;
        this.contentFsQuadMat.uniformsNeedUpdate = true;
      }
    };
    this.scene.add( this.contentFsQuad );
  }

  // -------------------------------------------------------------- Render Loop
  timeUpdate() {
    if (!this.clock) {
      this.clock = new Clock(false);
      this.clock.start();
      this.play = 1;
      this.time = 0.0;
      this.timeDelta = 0.0;
    }
    this.timeDelta = this.clock.getDelta();
    this.timeDelta *= this.play;
    this.time += this.timeDelta;
  }

  animationLoop(timestamp, frame) {
    if (this.renderOverrideFunc !== null) {
      this.renderOverrideFunc(timestamp, frame);
      return;
    }
    this.timeUpdate();
    this.render(timestamp, frame);
  }

  render(timestamp, frame) {
    if (this.controls) {
      this.controls.update();
    }

    this.envUpdate();
    this.camerasUpdate();

    const content = this.contentCurrent();
    const contentReadyToRender = content && content.instance && content.instance.render;
    if (!contentReadyToRender) {
      // content is still loading, only render the base scene.
      this.renderer.render(this.scene, this.camera);
      return;
    }

    this.postProcessingInit(content);

    this.contentContainer.updateMatrixWorld(true);
    content.camera.copy(this.camera);
    content.scene.position.copy(this.contentContainer.position);
    content.scene.quaternion.copy(this.contentContainer.quaternion);
    content.scene.scale.copy(this.contentContainer.scale);

    if (content.instance.update) {
      content.instance.update();
    }

    this.renderContent(content);
  }

  renderContent(content) {
    let contentID = 0; // default contentID to render.
    if (content.instance) {
      if (content.instance.contentPerCubeFace) {
        const contentPerCubeFace = content.instance.contentPerCubeFace();
        if (contentPerCubeFace) {
          if (contentPerCubeFace.front) {
            // for cube content, need to specify the contentID of at least one face,
            // by default we chose the front face.
            contentID = contentPerCubeFace.front;
          }
        }
      }
    }

    const renderTargetSaved = this.renderer.getRenderTarget();
    const cameras = this.renderer.xr.getCamera().cameras;
    const isVR = ( cameras.length === 2 ) && this.renderer.xr.isPresenting;
    if ( isVR ) {
      if ( renderTargetSaved ) {
        const updateCameraAndRenderer = false; // VR: camera and renderer are already updated internally.
        this.resize(renderTargetSaved.width, renderTargetSaved.height, 1, updateCameraAndRenderer);
      }
    }

    this.renderer.setRenderTarget(this.renderTargetContent);
    this.renderer.clear();

    content.instance.render(contentID);

    if (this.postProcessing) {
      this.postProcessing.render();
    }

    this.renderer.setRenderTarget(renderTargetSaved);

    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.inputEventsKill();
  }

  // -------------------------------------------------------------- Resize
  resize(w = window.innerWidth, h = window.innerHeight, r = window.devicePixelRatio, updateCameraAndRenderer = true) {
    if (this.res.x === w && this.res.y === h && this.devicePixelRatio === r ) {
      return; // this resolution has already been set.
    }

    this.res.set(w, h);
    this.resPixels.set((w * r), (h * r));
    this.devicePixelRatio = r;

    if ( updateCameraAndRenderer ) {
      this.camera.aspect = this.res.x / this.res.y;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.res.x, this.res.y);
      this.renderer.setPixelRatio(r);
    }

    this.renderTargets.forEach(renderTarget => {
      renderTarget.setSize(this.resPixels.x, this.resPixels.y);
    });

    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.resize) {
          content.instance.resize(this.resPixels.x, this.resPixels.y);
        }
      }
    }

    if (this.postProcessing) {
      this.postProcessing.resize(this.resPixels.x, this.resPixels.y);
    }
  }

  resizeHandler() {
    if (this.resizeOverrideFunc !== null) {
      this.resizeOverrideFunc();
      return;
    }
    this.resize();
  }

  // -------------------------------------------------------------- Cameras
  camerasUpdate() {
    if (!this.cameras) {
      this.cameras = [];
    }
    let cameraIndex = 0;
    const cameraProbeOnBeforeRender = (renderer, scene, camera) => {
      if (this.cameras.length === cameraIndex) {
        this.cameras.push(camera);
      }
      cameraIndex += 1;
    };
    if (!this.cameraProbe) {
      this.cameraProbe = new Mesh(); // used for running a quick test render and saving the camera value.
      this.cameraProbe.frustumCulled = false; // always render, even if not in camera view.
    }
    this.cameraProbe.onBeforeRender = cameraProbeOnBeforeRender;
    this.renderer.render(this.cameraProbe, this.camera);
    if (this.cameras.length > cameraIndex && cameraIndex >= 1) { // do not remove all cameras, leave at least one.
      this.cameras.splice(cameraIndex, this.cameras.length - cameraIndex);
    }
  }

  // -------------------------------------------------------------- Post Processing
  postProcessingInit(content) {
    let postProcessingInit = true;
    postProcessingInit = postProcessingInit && !this.postProcessing;
    postProcessingInit = postProcessingInit && content.instance.postProcessing;
    if (postProcessingInit) {
      const postProcessingConfig = content.instance.postProcessing();
      if (postProcessingConfig) {
        let postProcessingGui = null;
        if (this.config.gui) {
          postProcessingGui = this.config.gui.addFolder('Post Processing');
        }
        this.postProcessing = new EJPostProcessing(
          this.renderer,
          this.renderTargetContent,
          postProcessingGui
        );
        this.postProcessing.addPass(postProcessingConfig);
      }
    }
  }

  postProcessingKill() {
    if (this.postProcessing) {
      this.postProcessing.dispose();
      this.postProcessing = null;
    }
  }

  // -------------------------------------------------------------- Input Events
  inputEventsInit() {
    document.addEventListener('keydown', this.inputHandler.bind(this));
    document.addEventListener('keyup', this.inputHandler.bind(this));
    document.addEventListener('pointerdown', this.inputHandler.bind(this));
    document.addEventListener('pointerup', this.inputHandler.bind(this));
    document.addEventListener('pointermove', this.inputHandler.bind(this));
  }

  inputEventsKill() {
    document.removeEventListener('keydown', this.inputHandler.bind(this));
    document.removeEventListener('keyup', this.inputHandler.bind(this));
    document.removeEventListener('pointerdown', this.inputHandler.bind(this));
    document.removeEventListener('pointerup', this.inputHandler.bind(this));
    document.removeEventListener('pointermove', this.inputHandler.bind(this));
  }

  inputHandler(event) {
    const content = this.contentCurrent();
    if (content && content.instance && content.instance[event.type]) {
      content.instance[event.type](event);
    }
  }

  // -------------------------------------------------------------- Environment
  envKill() {
    if (this.env == null) {
      return;
    }
    if (this.env.container.parent) {
      this.env.container.parent.remove(this.env.container);
    }
    this.env = null;
  }

  envCameraInit() {
    this.env = new EJEnvCamera(new Object3D(), this.camera, this.cameraTexture);
    this.scene.add(this.env.container);
  }

  envInit(envType) {
    this.envKill();

    if (envType == EJEnvTypeNone) {
      return;
    } else if (envType == EJEnvTypeImage) {
      this.env = new EJEnv(new Object3D(), this.camera);
    }
    this.scene.add(this.env.container);
  }

  envUpdate() {
    if (this.env) {
      this.env.update();
    }
  }

  // -------------------------------------------------------------- Content
  contentLoaded() {
    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.loaded) {
          const loaded = content.instance.loaded();
          return loaded;
        }
      }
      return true; // if content exists, assume it is loaded.
    }
    return false;
  }

  contentLoadProgress() {
    let progress = 0;
    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.loadProgress) {
          progress = content.instance.loadProgress();
        }
      }
    }
    return progress;
  }

  contentBox( boxOut ) {
    const content = this.contentCurrent();
    if (content) {
      if (content.instance) {
        if (content.instance.box) {
          const box = content.instance.box();
          if ( box instanceof Box3 ) {
            boxOut.copy( box );
          }
        }
      }
    }
  }

  contentCurrent() {
    if (this.contents.length == 0) {
      return null;
    }
    const content = this.contents[this.contents.length - 1];
    return content;
  }

  contentDispose(content) {
    if (content.instance) {
      if (content.instance.dispose) {
        content.instance.dispose();
      }
    }
    if (content.scene) {
      EJUtils.disposeRecursive(content.scene);
    }
    if (content.gui) {
      content.gui.destroy();
    }
    this.postProcessingKill();
  }

  contentInstantiate(module) {
    const content = this.contentCurrent();
    if (content == null) {
      return;
    }

    content.scene = new Scene();
    content.scene.name = 'Content-Scene';
    content.camera = this.camera;
    if (this.config.gui) {
      content.gui = this.config.gui.addFolder('Content Settings');
    }

    const urlModifier = url => {
      const path = content.filename;
      let pathModified = path.substring(0, path.lastIndexOf('/')) + '/' + url;
      return pathModified;
    };
    this.loadingManager.setURLModifier(urlModifier);

    content.instance = module.main({
      canvas: this.canvas,
      renderer: this.renderer,
      renderTarget: this.renderTargetContent,
      scene: content.scene,
      camera: content.camera,
      loadingManager: this.loadingManager,
      gui: content.gui,
    });
  }

  contentKill(content) {
    this.contentDispose(content);
  }

  contentKillAll() {
    this.contents.forEach(content => {
      this.contentKill(content);
    });
    this.contents.splice(0, this.contents.length); // clear contents array.
  }

  contentValidate(_filename) {
    // TODO: do some checks on the file being loaded, like is it a JS file etc.
    return true;
  }

  // -------------------------------------------------------------- API
  setContent({ filename, module }) {
    if (this.contentValidate(filename) == false) {
      return; // content file is not valid.
    }

    this.contentKillAll();

    const content = {
      filename: filename,
      identifier: filename.split('.js')[0] + '-script',
      module,
      instance: null,
      scene: null,
      gui: null,
    };
    this.contents.push(content);

    this.contentInstantiate(module);
  }

  setCube(value) {
    //
  }

  getPostProcessing() {
    return this.postProcessing;
  }

  setEnvironment(value) {
    this.envInit(value);
  }
}

/* eslint-disable max-len */

// ---------------------------------------------------------------- EJPlayerCubeOne
class EJPlayerCubeOne extends EJPlayerBase {
  constructor(config) {
    super(config);
  }

  sceneInit() {
    this.cubeContainer = new Object3D();
    this.cube = new EJCube1(this.cubeContainer, this.config.gui);
    this.cube.cubeBack.onBeforeRender = ( renderer, scene, camera ) => {
      const cameras = this.renderer.xr.getCamera().cameras;
      const isVR = ( cameras.length === 2 ) && this.renderer.xr.isPresenting;
      if ( isVR ) {
        (cameras[0] === camera);
      }
      //
      this.cubeContainer.position.copy(this.contentContainer.position);
      this.cubeContainer.quaternion.copy(this.contentContainer.quaternion);
      this.cubeContainer.scale.copy(this.contentContainer.scale);
      //
      this.cube.updateMasterOpacity( this.masterOpacity );
      this.cube.updateProps( this.renderTargetContent.texture, this.resPixels );
    };
    this.scene.add( this.cubeContainer );
  }

  setCubeConfig(value) {
    // const sampleValue = {
    //   borderOn: true,
    //   borderWidth: 0.0025,
    //   borderFeather: 0.5,
    //   borderColor: new Color( 1.0, 1.0, 1.0 ),
    //   borderAlpha: 0.25,
    // };
    this.cube.setConfig( value );
  }

  contentBox( boxOut ) {
    if (!this.boxDefault) {
      const boxCenter = new THREE.Vector3(0, 0, 0);
      const boxSize = new THREE.Vector3(1, 1, 1);
      this.boxDefault = new Box3();
      this.boxDefault.setFromCenterAndSize( boxCenter, boxSize );
    }
    boxOut.copy( this.boxDefault ); // cubes are always 1 unit box with center at zero.
  }
}

const getAbsolutePosition = object => {
  const position = new Vector3();
  object.updateMatrixWorld();
  object.getWorldPosition(position);
  return position;
};
const getAbsoluteQuaternion = object => {
  const quaternion = new Quaternion();
  object.updateMatrixWorld();
  object.getWorldQuaternion(quaternion);
  return quaternion;
};
const getAbsoluteRotation = object => {
  const rotation = new Euler();
  object.updateMatrixWorld();
  rotation.setFromQuaternion(getAbsoluteQuaternion(object));
  return rotation;
};

class CameraTeleport {
  constructor() {
    this.raycaster = new Raycaster();
  }

  update(rotation, position, oldPosition, portalsByGroup, worlds) {
    const movementVector = position.clone().sub(oldPosition);
    const collision = this.checkCollision(position, movementVector, portalsByGroup);
    if (!collision) return null;
    const sourcePortal = collision.portal;
    const targetWorld = worlds[sourcePortal.targetWorldId];
    const targetPortal = targetWorld.portals[sourcePortal.targetPortalId];

    const cameraRotation = rotation.clone();
    const targetPortalRotation = getAbsoluteRotation(targetPortal.group);
    const sourcePortalRotation = getAbsoluteRotation(sourcePortal.group);
    const deltaRotation = new Euler(
      targetPortalRotation.x - sourcePortalRotation.x,
      targetPortalRotation.y - sourcePortalRotation.y + Math.PI,
      targetPortalRotation.z - sourcePortalRotation.z,
      'YXZ'
    );

    cameraRotation.x += deltaRotation.x;
    cameraRotation.y += deltaRotation.y;
    cameraRotation.z += deltaRotation.z;

    return {
      newWorld: worlds[targetWorld.id],
      newPosition: collision.intersectionPoint // take the intersection point
        .sub(getAbsolutePosition(sourcePortal.group)) // by removing the portal position we'll have the delta vector between the intersection and the portal position
        .applyEuler(deltaRotation) // rotate this vector by the teleport rotation
        .add(collision.movementVector.clone()
          .multiplyScalar(1 - collision.ratioToPosition)
          .applyEuler(deltaRotation)) // add the remaining movement vector, rotated by the teleport rotation
        .add(getAbsolutePosition(targetPortal.group)), // move all of this to the target portal position
      newRotation: cameraRotation,
    };
  }

  // In case of collision, it returns the ratio of the "movement" vector to the hit point
  // If the collision is at the third of the "movement" vector, then it will return 0.33
  // If there is no collision, it will return null
  checkCollision(origin, movementVector, portalsByGroup) {
    this.raycaster.set(origin, movementVector);
    this.raycaster.far = movementVector.length();
    const intersections = this.raycaster.intersectObjects(
      Object.values(portalsByGroup).map(portal => portal.group),
      true
    );

    if (intersections.length) {
      const intersection = intersections[0];
      return {
        ratioToPosition: intersection.distance / this.raycaster.far,
        portal: portalsByGroup[intersection.object.uuid],
        intersectionPoint: intersection.point,
        movementVector,
      };
    }
    return null;
  }
}

/* eslint-disable max-len */

// ---------------------------------------------------------------- EJPlayerPortals

const MAX_PORTAL_RENDERING_RECURSION_LEVEL = 4;

class EJPlayerPortals extends EJPlayerBase {
  constructor(config) {
    super(config);
    this.scenes = [];

    // Cached variables to avoid creating on every render multiple times
    this.tmpScene = new Group();
    this.originalCameraMatrixWorld = new Matrix4();
    this.originalCameraProjectionMatrix = new Matrix4();

    this.tmpRotationYMatrix = new Matrix4().makeRotationY(Math.PI);
    this.tmpInverse = new Matrix4();
    this.tmpDstInverse = new Matrix4();
    this.tmpSrcToCam = new Matrix4();
    this.tmpSrcToDst = new Matrix4();
    this.tmpResult = new Matrix4();

    this.tmpDstRotationMatrix = new Matrix4();
    this.tmpNormal = new Vector3();
    this.tmpClipPlane = new Plane();
    this.tmpClipVector = new Vector4();
    this.tmpQ = new Vector4();
    this.tmpProjectionMatrix = new Matrix4();
    this.tmpCameraInverseViewMat = new Matrix4();

    this.cameraPreviousPosition = new Vector3();
    this.cameraTeleport = new CameraTeleport();
    this.cameraDeltaPosition = new Vector3(0, 0, 0);
    this.cameraDeltaRotation = new Euler(0, 0, 0);

    this.xrScene = new Scene();
    this.xrScene.rotation.order = 'YXZ';
    this.xrObject = new Object3D();
    this.xrObject.rotation.order = 'YXZ';
    this.xrScene.add(this.xrObject);
  }

  // -------------------------------------------------------------- Portals

  setContent({ filename, module }) {
    super.setContent({ filename, module });
    this.worlds = this.contentCurrent().instance.worlds;
    this.currentWorld = this.contentCurrent().instance.currentWorld;

    this.scenes = this.contentCurrent().scene.children;
    Object.values(this.worlds).forEach(world => {
      Object.values(world.portals).forEach(portal => this.scenes[world.id].add(portal.group));
    });
  }

  // -------------------------------------------------------------- Render
  renderContent(content) {
    for (const camera of this.cameras) {
      // You can alter the camera position and rotation
      // Take into account that we might teleported to another room,
      // use this.cameraDeltaPosition and this.cameraDeltaRotation to transform the VR camera absolute space
      // to our local space

      // const cameraPosition = new Vector3().setFromMatrixPosition(camera.matrixWorld);
      // const cameraRotation = new Euler().setFromRotationMatrix(camera.matrixWorld);
      // this.xrObject.position.copy(cameraPosition).sub(this.cameraDeltaPosition);
      // this.xrObject.rotation.copy(cameraRotation);
      // this.xrScene.rotation.copy(this.cameraDeltaRotation); // TODO: should be negative
      // this.xrScene.updateWorldMatrix(false, true);
      // camera.position.copy(getAbsolutePosition(this.xrObject));
      // camera.rotation.copy(getAbsoluteRotation(this.xrObject));
      // camera.updateMatrix();
      // camera.updateWorldMatrix();
      // camera.updateProjectionMatrix();

      this.checkCameraToTeleport(camera);

      this.cameraPreviousPosition.copy(camera.position);

      this.renderWorldsAndPortals(
        this.worlds,
        this.currentWorld,
        camera,
        camera.matrixWorld,
        camera.projectionMatrix
      );


      // Post processing?
    }
  }


  checkCameraToTeleport(camera) {
    const currentWorldPortalGroups = Object.values(this.currentWorld.portals).reduce((result, portal) => {
      result[portal.group.children[0].uuid] = portal;
      return result;
    }, {});
    const collision = this.cameraTeleport.update(
      camera.rotation,
      camera.position,
      this.cameraPreviousPosition,
      currentWorldPortalGroups,
      this.worlds
    );
    if (collision) {
      this.currentWorld = collision.newWorld;
      this.controls.setPosition(collision.newPosition);
      this.controls.setRotation(collision.newRotation);
      this.cameraPreviousPosition.copy(collision.newPosition);
      this.cameraDeltaPosition.copy(collision.newPosition);
      this.cameraDeltaRotation.copy(collision.newRotation);
    }
  }

  renderWorldsAndPortals(
    worlds,
    world,
    camera,
    viewMat,
    projMat,
    recursionLevel = 0,
    excludePortal
  ) {
    const recursionLevelLeft = MAX_PORTAL_RENDERING_RECURSION_LEVEL - recursionLevel;
    const portalsInWorld = Object.values(world.portals);

    for (let i = 0; i < portalsInWorld.length; i++) {
      const portal = portalsInWorld[i];
      if (portal === excludePortal) continue;
      const destinationWorld = worlds[portal.targetWorldId];

      this.gl.colorMask(false, false, false, false);
      this.gl.depthMask(false);
      this.gl.disable(this.gl.DEPTH_TEST);
      this.gl.enable(this.gl.STENCIL_TEST);
      this.gl.stencilFunc(this.gl.NOTEQUAL, recursionLevel, 0xFF);
      this.gl.stencilOp(this.gl.INCR, this.gl.KEEP, this.gl.KEEP);
      this.gl.stencilMask(0xFF);
      this.renderScene(camera, portal.group.children, viewMat, projMat);

      const destViewMat = this.computePortalViewMatrix(worlds, portal, viewMat).clone();
      const destProjMat = this.computePortalProjectionMatrix(worlds, portal, destViewMat, projMat).clone();

      if (recursionLevelLeft > 0 && Object.values(destinationWorld.portals).length > 1) {
        // Go inside the portal recursively and start the function from the top
        this.renderWorldsAndPortals(
          worlds,
          destinationWorld,
          camera,
          destViewMat,
          destProjMat,
          recursionLevel + 1,
          portal
        );
      } else {
        // When we reach to the last portal, we just render the world
        this.gl.colorMask(true, true, true, true);
        this.gl.depthMask(true);
        this.renderer.clear(false, true, false);
        this.gl.enable(this.gl.DEPTH_TEST);
        this.gl.enable(this.gl.STENCIL_TEST);
        this.gl.stencilMask(0x00);
        this.gl.stencilFunc(this.gl.EQUAL, recursionLevel + 1, 0xFF);
        this.renderScene(
          camera,
          this.scenes[destinationWorld.contentId].children,
          destViewMat,
          destProjMat
        );
      }

      // Mask out the rendered world through the portal, regardless if it was the last recursion or an intermittent one
      this.gl.colorMask(false, false, false, false);
      this.gl.depthMask(false);
      this.gl.enable(this.gl.STENCIL_TEST);
      this.gl.stencilMask(0xff);
      this.gl.stencilFunc(this.gl.NOTEQUAL, recursionLevel + 1, 0xFF);
      this.gl.stencilOp(this.gl.DECR, this.gl.KEEP, this.gl.KEEP);
      this.renderScene(camera, portal.group.children, viewMat, projMat);
    }

    // When all the content are rendered for every portal in this world, render the mask of the portals
    this.gl.disable(this.gl.STENCIL_TEST);
    this.gl.stencilMask(0x00);
    this.gl.colorMask(false, false, false, false);
    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.depthMask(true);
    this.gl.depthFunc(this.gl.ALWAYS);
    this.renderer.clear(false, true, false);
    this.renderScene(
      camera,
      portalsInWorld.map(portal => portal.group),
      viewMat,
      projMat
    );

    // At last, render the main world
    this.gl.depthFunc(this.gl.LESS);
    this.gl.enable(this.gl.STENCIL_TEST);
    this.gl.stencilMask(0x00);
    this.gl.stencilFunc(this.gl.LEQUAL, recursionLevel, 0xFF);
    this.gl.colorMask(true, true, true, true);
    this.gl.depthMask(true);
    this.renderScene(
      camera,
      this.scenes[world.contentId].children,
      viewMat,
      projMat
    );
  }

  renderScene(camera, children, viewMat, projMat) {
    this.tmpScene.children = children;
    this.originalCameraMatrixWorld.copy(camera.matrixWorld);
    this.originalCameraProjectionMatrix.copy(camera.projectionMatrix);
    camera.matrixAutoUpdate = false;
    camera.matrixWorld.copy(viewMat);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    camera.projectionMatrix.copy(projMat);
    // need to disable xr so we could use our custom camera object to render at an arbitrary position
    // but it removes the stereo effect
    this.renderer.xr.enabled = false;
    this.renderer.render(this.tmpScene, camera);
    this.renderer.xr.enabled = true;
    camera.matrixAutoUpdate = true;
    camera.matrixWorld.copy(this.originalCameraMatrixWorld);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    camera.projectionMatrix.copy(this.originalCameraProjectionMatrix);
  }

  computePortalViewMatrix(worlds, sourcePortal, viewMat) {
    this.tmpSrcToCam.multiplyMatrices(this.tmpInverse.copy(viewMat).invert(), sourcePortal.group.matrixWorld);
    const destinationPortal = worlds[sourcePortal.targetWorldId].portals[sourcePortal.targetPortalId];
    this.tmpDstInverse.copy(destinationPortal.group.matrixWorld).invert();
    this.tmpSrcToDst.identity().multiply(this.tmpSrcToCam).multiply(this.tmpRotationYMatrix).multiply(this.tmpDstInverse);
    this.tmpResult.copy(this.tmpSrcToDst).invert();
    return this.tmpResult;
  }

  // Use custom projection matrix to align portal camera's near clip plane with the surface of the portal
  // See http://www.terathon.com/code/oblique.html
  // See www.terathon.com/lengyel/Lengyel-Oblique.pdf
  computePortalProjectionMatrix(worlds, sourcePortal, viewMat, projMat) {
    const destinationPortal = worlds[sourcePortal.targetWorldId].portals[sourcePortal.targetPortalId];
    this.tmpDstRotationMatrix.identity().extractRotation(destinationPortal.group.matrixWorld);
    this.tmpNormal.set(0, 0, 1).applyMatrix4(this.tmpDstRotationMatrix);
    const destinationPortalAbsolutePosition = new Vector3();
    destinationPortal.group.getWorldPosition(destinationPortalAbsolutePosition);
    this.tmpClipPlane.setFromNormalAndCoplanarPoint(this.tmpNormal, destinationPortalAbsolutePosition);
    this.tmpCameraInverseViewMat.copy(viewMat).invert();
    this.tmpClipPlane.applyMatrix4(this.tmpCameraInverseViewMat);

    this.tmpClipVector.set(
      this.tmpClipPlane.normal.x,
      this.tmpClipPlane.normal.y,
      this.tmpClipPlane.normal.z,
      this.tmpClipPlane.constant
    );
    this.tmpProjectionMatrix.copy(projMat);

    this.tmpQ.x = (Math.sign(this.tmpClipVector.x) + this.tmpProjectionMatrix.elements[8]) / this.tmpProjectionMatrix.elements[0];
    this.tmpQ.y = (Math.sign(this.tmpClipVector.y) + this.tmpProjectionMatrix.elements[9]) / this.tmpProjectionMatrix.elements[5];
    this.tmpQ.z = -1.0;
    this.tmpQ.w = (1.0 + this.tmpProjectionMatrix.elements[10]) / projMat.elements[14];

    this.tmpClipVector.multiplyScalar(2 / this.tmpClipVector.dot(this.tmpQ));

    this.tmpProjectionMatrix.elements[2] = this.tmpClipVector.x;
    this.tmpProjectionMatrix.elements[6] = this.tmpClipVector.y;
    this.tmpProjectionMatrix.elements[10] = this.tmpClipVector.z + 1.0;
    this.tmpProjectionMatrix.elements[14] = this.tmpClipVector.w;

    return this.tmpProjectionMatrix;
  }
}

if (typeof window !== "undefined") {
  window.EJPlayer = EJPlayer;
  window.EJPlayerBase = EJPlayerBase;
  window.EJPlayerCubeOne = EJPlayerCubeOne;
  window.EJPlayerPortals = EJPlayerPortals;
  window.EJWebGLRenderer = EJWebGLRenderer$1;
  window.EJPostProcessing = EJPostProcessing;
}

export { EJPlayer, EJPlayerBase, EJPlayerCubeOne, EJPlayerPortals, EJPostProcessing, EJWebGLRenderer$1 as EJWebGLRenderer };
